/**
 * @ngdoc module
 * @module form
 * @name form
 * @description
 *
 *   Utilities for form management and dynamic forms.
 *
 *   Composed of {@link ../form.directives/index.html form.directives}, {@link ../form.services/index.html form.services} and {@link ../form.filters/index.html form.filters}.
 *
 */

angular.module('form', [
  'form.services',
  'form.directives',
  'form.filters'
]);
/**
 * @ngdoc module
 * @module form
 * @name form.directives
 * @description
 *
 *   Form directives. Fields by types, validation mechanisms, dynamic forms, etc.
 *
 *   This module is a part of {@link ../form/index.html form} and uses {@link ../form.services/index.html form.services}.
 *
 */

angular.module('form.directives', [
  'form.templates',
  'form.filters',
  'form.services',
  'kendo.directives',
  'ui.utils',
  'pascalprecht.translate',
  'ui.bootstrap.typeahead',
  'ui.bootstrap.tpls',
  'mgcrea.ngStrap.tooltip',
  'ngSanitize',
  'ngTagsInput'
]).config(['tagsInputConfigProvider', function(tagsInputConfigProvider) {
  'use strict';
  // Watch tags-input's placeholder
  tagsInputConfigProvider.setActiveInterpolation('tagsInput', { placeholder: true });
}]);

/**
 * @ngdoc directive
 * @area api
 * @module form.directives
 * @name bool-radio-field
 * @scope
 * @restrict EA
 * @description
 *
 *   A basic boolean field for dynamic forms.
 *
 *   Uses validation-field directive for automatic layout, configuration and labels management.
 *
 * @param {string} name - The name of the field in the current form and in the model
 * @param {object} viewConfiguration - A view configuration as returned by the resource-manager service for example
 * @param {object} configuration - The configuration of the field extracted from a viewConfiguration object (in replacement to viewConfiguration)
 * @param {string} namespace - The configuration of the field extracted from a viewConfiguration object (in replacement to viewConfiguration)
 * @param {String} model - The model to bind to
 * @param {boolean} bare - Specify that the field should be left alone without label and help messages
 *
 * @example
 *
 *   `<bool-field name="demandeurIsBeneficiaire" view-configuration="identificationDemandeurConfiguration" data="persistence"></bool-field>`
 *
 */
angular.module('form.directives').directive('boolField', ['$timeout', 'formUtils',

  function($timeout, formUtils) {
    'use strict';

    return {
      restrict: 'EA',
      replace: true,
      templateUrl: 'form/form-directives/bool-field/bool-field.html',
      scope: {
        name: '@',
        viewConfiguration: '=',
        config: '=configuration',
        ns: '=namespace',
        model: '=',
        valueTrue: '=',
        valueFalse: '=',
        bare: '=',
        readOnly: '=',
        remoteValidation: '=?',
        onChange: '&?',
        labelClass: '=?'
      }, link: function(scope) {
        scope.configuration = scope.config || _.get(scope, 'viewConfiguration.fields.' + scope.name) || {};
        scope.namespace = scope.ns || scope.configuration.ns || _.get(scope, 'viewConfiguration.ns');
				
        // Read remote validation from directive, configuration or viewConfiguration
        scope.remoteValidation = scope.remoteValidation !== undefined ? scope.remoteValidation :
          scope.configuration.remoteValidation !== undefined ? scope.configuration.remoteValidation :
            _.get(scope, 'viewConfiguration.remoteValidation') !== undefined ? _.get(scope, 'viewConfiguration.remoteValidation') : false;

        formUtils.wrapPrimitiveInObject(scope, 'model', 'data');

        // Callback on changes
        scope.change = function() {
          if (scope.onChange) {
            // Must wait scope to update
            $timeout(scope.onChange, 0);
          }
        };
      }
    };
  }
]);

/**
 * @ngdoc directive
 * @area api
 * @module form.directives
 * @name check-validation-field
 * @restrict A
 * @description
 *
 *   Applies a simple validation rule to a form field.
 *
 *   Takes an angular expression and evaluates it in the current scope, if it returns false the field will be set to invalid.
 *
 * @param {string} checkValidationField - The expression to evaluate
 *
 * @example
 *
 *   `<input type="text" ng-model="myModel" check-validation-field="myModel.length < 10">`
 *
 */

angular.module('form.directives').directive('checkValidationField', [

  function() {
    'use strict';
    return {
      require: 'ngModel',
      link: function(scope, elem, attrs, ctrl) {

        // if ngModel is not defined, we do nothing
        if (!ctrl) {
          return;
        }
        if (!attrs.checkValidationField) {
          return;
        }

        var watch = scope.$watch(attrs.checkValidationField, function(isValid) {
          ctrl.$setValidity('checkValidity', isValid);
        });

        scope.$on('$destroy',function () {
          watch();
        });
      }
    };
  }
]);

/**
 * @ngdoc directive
 * @area api
 * @module form.directives
 * @name confirmation-field
 * @restrict A
 * @description
 *
 *   When 2 fields must have the same value (generally password or emails), check if the 2 values given are identical.
 *
 *
 *   Inspiration from : http://stackoverflow.com/questions/14012239/password-check-directive-in-angularjs#nxEqual
 *
 *   Note : quite redundant with validation-field directive
 *
 * @param {string} validation-field - The expression that should be equal to the value of the current model
 *
 * @example
 *
 *   `<input type="text" ng-model="password1" confirmation-field="password2">`
 *
 */

angular.module('form.directives').directive('confirmationField', [
  function() {
    'use strict';
    return {
      require: ['ngModel'],
      link: function(scope, elem, attrs, ctrls) {

        var modelCtrl = ctrls[0];
        // if ngModel is not defined, we do nothing
        if (!modelCtrl) {
          return;
        }
        if (!attrs.confirmationField) {
          return;
        }

        scope.$watch(attrs.confirmationField, function(value) {
          // the second value is not set yet, we do nothing
          if (modelCtrl.$viewValue === undefined || modelCtrl.$viewValue === '') {
            return;
          }
          modelCtrl.$setValidity('confirmationField', value === modelCtrl.$viewValue);
        });
        modelCtrl.$parsers.push(function(value) {
          var isValid = value === scope.$eval(attrs.confirmationField);
          modelCtrl.$setValidity('confirmationField', isValid);
          // Affectation de la valeur, même en cas d'erreur (contraire au principe Angular)
          // Cela permet de résoudre un problème lors de la validation de 2 champs email
          // cf http://jira.mgdis.fr/browse/CRPLPDA-530
          return value;
        });
      }
    };
  }
]);
/**
 * @ngdoc directive
 * @area api
 * @module form.directives
 * @name date-field
 * @scope
 * @restrict EA
 * @description
 *
 *   A basic date field for dynamic forms.
 *
 *   Uses validation-field directive for automatic layout, configuration and labels management.
 *
 *   Uses kendo-ui datePicker directive
 *
 * @param {string} name - The name of the field in the current form and in the model
 * @param {object} viewConfiguration - A view configuration as returned by the resource-manager service for example
 * @param {object} configuration - The configuration of the field extracted from a viewConfiguration object (in replacement to viewConfiguration)
 * @param {string} namespace - The configuration of the field extracted from a viewConfiguration object (in replacement to viewConfiguration)
 * @param {String} model - The model to bind to
 * @param {boolean} bare - Specify that the field should be left alone without label and help messages
 * @param {object} datePickerConfig - Customised config of kendo datePicker
 *
 * @example
 *
 *   `<date-field name="dateNaissance" view-configuration="identificationDemandeurConfiguration" data="demandeur"></date-field>`
 *
 */

angular.module('form.directives').directive('dateField', ['dateUtils', '$timeout',

  function(dateUtils, $timeout) {
    'use strict';

    return {
      restrict: 'EA',
      replace: true,
      templateUrl: 'form/form-directives/date-field/date-field.html',
      require: '^form',
      scope: {
        name: '@',
        viewConfiguration: '=',
        config: '=configuration',
        ns: '=namespace',
        model: '=',
        bare: '=',
        datePickerConfig: '=',
        isoString: '=',
        readOnly: '=',
        dateOnly: '=',
        remoteValidation: '=?',
        labelClass: '=?'
      },
      link: function(scope) {

        scope.forceRefresh = true;
        var watches = [];
        var timer;

        // Convert date string from json to Date (except if dateOnly mode, we need to keep date string format)
        if (!scope.dateOnly && typeof(scope.model) === 'string' && !scope.isoString) {
          scope.model = moment(scope.model).toDate();
        }

        watches.push(scope.$watch('[config, viewConfiguration]', function() {
          scope.configuration = scope.config || _.get(scope, 'viewConfiguration.fields.' + scope.name) || {};
          scope.configuration.type = 'date';
          scope.namespace = scope.ns || scope.configuration.ns || _.get(scope, 'viewConfiguration.ns');

          // Read remote validation from directive, configuration or viewConfiguration
          scope.remoteValidation = scope.remoteValidation !== undefined ? scope.remoteValidation :
            scope.configuration.remoteValidation !== undefined ? scope.configuration.remoteValidation :
              _.get(scope, 'viewConfiguration.remoteValidation') !== undefined ? _.get(scope, 'viewConfiguration.remoteValidation') : false;
        }, true));

        function getDisplayModel(dateModel) {
          var displayModel = null;
          if(_.isDate(dateModel)){
            if(scope.isoString){
              displayModel = kendo.toString(dateModel, 'yyyy-MM-dd');
            }else{
              displayModel = kendo.toString(dateModel, 'u');
            }
          }

          return displayModel;
        }

        // Indicates if the model was deleted from this directive
        var modelDeleted = false;

        watches.push(scope.$watch('model', function() {
          var dateModel = scope.model ? moment(scope.model).toDate() : null;
          scope.dateField = {
            displayModel: getDisplayModel(dateModel),
            kModel: dateModel,
            datePickerConfig: scope.datePickerConfig ?
              scope.datePickerConfig : {
                format: 'd',
                culture: 'fr-FR'
              }
          };

          // Min & max based on configuration.restrictions
          dateUtils.setMinRestriction(scope.configuration.restrictions, scope.dateField);
          dateUtils.setMaxRestriction(scope.configuration.restrictions, scope.dateField);

          // Min & max based on configuration.restrictions.minDuration && configuration.restrictions.maxDuration
          if(scope.configuration && scope.configuration.restrictions) {
            scope.configuration.restrictions.filter(function(restriction){
              return (restriction.hasOwnProperty('maxDurationInclusive') ||
                restriction.hasOwnProperty('minDurationInclusive') ||
                restriction.hasOwnProperty('maxDurationExclusive') ||
                restriction.hasOwnProperty('minDurationExclusive'));
            }).forEach(function(restriction){
              dateUtils.setDurationRestriction(restriction, scope.dateField);
            });
          }

          // dirty dirty ! force refreshing of the kendo-ui directive so that configuration modifications are applied.
          if (!modelDeleted) {
            scope.forceRefresh = false;
            timer = $timeout(function() {
              scope.forceRefresh = true;
              scope.$digest();
            }, 1);
          }
          else {
            modelDeleted = false;
          }

        }, true));

        watches.push(scope.$watch('dateField.kModel', function() {
          if (scope.dateField && scope.dateField.kModel !== null) {
            if (scope.dateOnly) {
              scope.model = moment(scope.dateField && scope.dateField.kModel).format('YYYY-MM-DD');
            } else {
              scope.model = scope.isoString ? scope.dateField.kModel.toISOString() : scope.dateField.kModel;
            }
          }
        }));

        watches.push(scope.$watch('dateField.displayModel', function() {
          // Delete model when field has been emptied
          if (scope.dateField && !scope.dateField.displayModel && scope.model) {
            delete scope.model;
            modelDeleted = true;
          }
        }));

        scope.validateDateField = function() {
          // Case where the field was modified but not its model, it means that kendo rejected its format
          if (scope.dateField && scope.dateField.displayModel && !scope.dateField.kModel) {
            return false;
          }
          // Case where the value doesn't respect min and max constraints. Possible if the user doesn't use the datePicker.
          if (scope.dateField && scope.dateField.datePickerConfig.max && scope.model > scope.dateField.datePickerConfig.max) {
            return false;
          }
          if (scope.dateField && scope.dateField.datePickerConfig.min && scope.model < scope.dateField.datePickerConfig.min) {
            return false;
          }
          return true;

        };

        scope.$on('$destroy', function() {
          $timeout.cancel(timer);
          _.each(watches, function(watch) {
            watch();
          });
        });
      }
    };
  }
]);

angular.module('form.directives').directive('datetimeField', ['dateUtils', '$timeout',

  function(dateUtils, $timeout) {
    'use strict';

    return {
      restrict: 'EA',
      replace: true,
      templateUrl: 'form/form-directives/datetime-field/datetime-field.html',
      require: '^form',
      scope: {
        name: '@',
        viewConfiguration: '=',
        config: '=configuration',
        ns: '=namespace',
        model: '=', 
        bare: '=',
        datePickerConfig: '=',
        readOnly: '=',
        remoteValidation: '=?',
        labelClass: '=?'
      },
      link: function(scope) {

        scope.forceRefresh = true;
        var watches = [];
        var timer;

        watches.push(scope.$watch('[config, viewConfiguration]', function() {
          scope.configuration = scope.config || _.get(scope, 'viewConfiguration.fields.' + scope.name) || {};
          scope.namespace = scope.ns || scope.configuration.ns || _.get(scope, 'viewConfiguration.ns');

          // Read remote validation from directive, configuration or viewConfiguration
          scope.remoteValidation = scope.remoteValidation !== undefined ? scope.remoteValidation :
            scope.configuration.remoteValidation !== undefined ? scope.configuration.remoteValidation :
              _.get(scope, 'viewConfiguration.remoteValidation') !== undefined ? _.get(scope, 'viewConfiguration.remoteValidation') : false;
        }, true));

        watches.push(scope.$watch('model', function() {
          var ngModel = null;
          if (scope.model) {
            ngModel = moment.isDate(scope.model) ? scope.model : new Date(scope.model);
          }
          scope.dateField = {
            ngModel: kendo.toString(ngModel, 'dd/MM/yyyy HH:mm'),
            // "datePickerConfig" correspond à l'attribut "k-options" du composant "kendo-date-time-picker" :
            datePickerConfig: scope.datePickerConfig ?
              scope.datePickerConfig: {
                culture: 'fr-FR',
                format: 'dd/MM/yyyy HH:mm', // Format d'affichage de la date dans le champ de saisie.
                timeFormat: 'HH:mm' // Format d'affichage de l'heure dans la liste déroulante.
              }
          };

          // Min & max based on configuration.restrictions
          dateUtils.setMinRestriction(scope.configuration.restrictions, scope.dateField);
          dateUtils.setMaxRestriction(scope.configuration.restrictions, scope.dateField);

          // Min & max based on configuration.restrictions.minDuration && configuration.restrictions.maxDuration
          if(scope.configuration && scope.configuration.restrictions) {
            scope.configuration.restrictions.filter(function(restriction){
              return (restriction.hasOwnProperty('maxDurationInclusive') || 
                restriction.hasOwnProperty('minDurationInclusive') ||
                restriction.hasOwnProperty('maxDurationExclusive') ||
                restriction.hasOwnProperty('minDurationExclusive'));
            }).forEach(function(restriction){
              dateUtils.setDurationRestriction(restriction, scope.dateField); 
            });
          }

          // dirty dirty ! force refreshing of the kendo-ui directive so that configuration modifications are applied.
          scope.forceRefresh = false;
          timer = $timeout(function() {
            scope.forceRefresh = true;
            scope.$digest();
          }, 1);

        }, true));

        scope.validateDateField = function() {
          // Case where the value doesn't respect min and max constraints. Possible if the user doesn't use the datePicker.
          if (scope.dateField.datePickerConfig.max && (scope.model > scope.dateField.datePickerConfig.max)) {
            return false;
          }
          if (scope.dateField.datePickerConfig.min && (scope.model < scope.dateField.datePickerConfig.min)) {
            return false;
          }
          return true;
        };

        // Read user input
        watches.push(scope.$watch('dateField.ngModel', function() {
          var regexIsDateTime = /^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}$/;
          if (regexIsDateTime.test(scope.dateField.ngModel)) {
            scope.model = moment(scope.dateField.ngModel, 'DD/MM/YYYY HH:mm').toDate();
          }
        }));

        scope.$on('$destroy', function() {

          $timeout.cancel(timer);
          _.each(watches, function(watch) {
            watch();
          });
        });
      }
    };
  }
]);

/**
 * @ngdoc directive
 * @area api
 * @module form.directives
 * @name dynamic-field
 * @scope
 * @restrict EA
 * @description
 *
 *   A form field dynamically created from declarative options.
 *
 *   Uses specialized directives (text-field, number-field, etc.) according to the field dataType, data subtype or presentation.
 *
 * @param {string} name - the name of the field, also used as the key to set in the 'data' parameter.
 * @param {String} model - The model to bind to
 * @param {object} presentation - The presentation used to render this field. TODO detailed description of format when matured.
 * @param {object} viewConfiguration - A view configuration as returned by the resource-manager service for example
 * @param {object} configuration - The configuration of the field extracted from a viewConfiguration object (in replacement to viewConfiguration)
 * @param {string} namespace - The configuration of the field extracted from a viewConfiguration object (in replacement to viewConfiguration)
 * @param {object} mdm - A mdm object as filled by the mdm service
 * @param {boolean} bare - Specify that the field should be left alone without label and help messages
 *
 */
angular.module('form.directives').directive('dynamicField', ['formUtils', '$http', '$translate', '$log', '$filter',
  function(formUtils, $http, $translate, $log, $filter) {
    'use strict';

    return {
      restrict: 'EA',
      templateUrl: 'form/form-directives/dynamic-field/dynamic-field.html',
      require: ['^form', '?^dynamicFormGroups'],
      scope: {
        name: '@',
        model: '=',
        viewConfiguration: '=',
        config: '=configuration',
        ns: '=namespace',
        presentation: '=',
        mdm: '=',
        bare: '=',
        labelClass: '=?',
        readOnly: '=',
        authUser: '=',
        authPass: '='
      },
      link: function(scope, element, attrs, controllers) {

        var watches = [];

        // Configuration
        scope.configuration = scope.config || _.get(scope, 'viewConfiguration.fields.' + scope.name) || {};

        // Merge presentation with configuration who can override some configuration's properties
        if (!_.isEmpty(scope.presentation)) {
          scope.configuration = _.merge(scope.configuration, scope.presentation);
        }

        var ordreOptions = _.get(scope.configuration, 'select.ordreOptions') || _.get(scope.viewConfiguration, 'select.ordreOptions');

        // Namespace
        scope.namespace = scope.ns || scope.configuration.ns;

        // Wrap primitive
        formUtils.wrapPrimitiveInObject(scope, 'model', 'data');

        // Access "scopeFields" element from directive "dynamic-form-group"
        var scopeFields;
        if (controllers && controllers[1] && controllers[1].scopeFields) {
          scopeFields = controllers[1].scopeFields;
        }

        // Read remote validation from configuration or viewConfiguration
        scope.remoteValidation = scope.configuration.remoteValidation !== undefined ? scope.configuration.remoteValidation :
          _.get(scope, 'viewConfiguration.remoteValidation') !== undefined ? _.get(scope, 'viewConfiguration.remoteValidation') : false;

        //  List of properties to analyse when we use angular expression on configuration's values properties.
        var properties = ['required', 'hidden', 'min', 'max', 'minlength', 'maxlength', 'pattern', 'minDuration', 'maxDuration'];

        // Clone the configuration for not override angular expression contains on some configuration's properties
        scope.dynamicFieldConfiguration = angular.copy(scope.configuration);
        scope.restrictionDataUrl = _.get(scope.dynamicFieldConfiguration, 'restrictions[0].dataURL');

        scope.selectField =
          (scope.dynamicFieldConfiguration.dataType === 'xs:string' || scope.dynamicFieldConfiguration.dataType === 'string') && !scope.dynamicFieldConfiguration.subType &&
          (!scope.dynamicFieldConfiguration.searchKey && !scope.dynamicFieldConfiguration.displayKey && (scope.restrictionDataUrl || scope.dynamicFieldConfiguration.dataURL));

        if (scope.selectField) {
          scope.source = scope.dynamicFieldConfiguration.dataURL && scope.mdm[scope.dynamicFieldConfiguration.dataURL] ||
            scope.dynamicFieldConfiguration.restrictions[0].dataURL;
        }

        //If there is not source, we go to search the source to dataSchemas
        if (!scope.source && (scope.dynamicFieldConfiguration.dataType === 'xs:string' ||
            scope.dynamicFieldConfiguration.dataType === 'string')) {

          if (_.get(scope, 'dynamicFieldConfiguration.schema.href')) {
            $http.get(scope.dynamicFieldConfiguration.schema.href)
              .then(function(response) {
                var datSchConf = _.get(response.data, 'results[0]');
                scope.dataSchemaConfiguration = datSchConf;

                // Propagation de la propriété dynamique 'required'
                // D'autres propriétés peuvent potentiellement être concernées
                _.set(scope, 'dataSchemaConfiguration.required', _.get(scope, 'dynamicFieldConfiguration.required'));

                if (_.get(datSchConf, 'restrictions[0].dataURL')) {
                  scope.selectField = false;

                  var firstDisplayKey = scope.getFirstDisplayKey(datSchConf.displayKey);
                  scope.ngOptions = 'item.' + (datSchConf.persistedKey || firstDisplayKey) + ' as item.' + firstDisplayKey + ' for item in dynamicSearch($viewValue)';
                  scope.source = scope.dataSchemaConfiguration.restrictions[0].dataURL;

                  // Bakcup help field, and replace its value to avoid ugly display
                  if (datSchConf.displayKey && datSchConf.displayKey.indexOf(',') !== -1 &&
                    scope.dynamicFieldConfiguration.helpTooltip) {
                    scope.helpViewConfigurationBackup = angular.copy(scope.dynamicFieldConfiguration.help);
                    scope.dynamicFieldConfiguration.help = ' ';
                  }

                  // If the model contains data at loading and several values are in displayKey, we need to get some other data from MDM
                  if (!_.isEmpty(scope.model) && datSchConf.displayKey && datSchConf.displayKey.indexOf(',') !== -1) {
                    scope.dynamicSearch(scope.model).then(function(results) {
                      if (results && results[0]) {
                        scope.typeaheadOnSelect(results[0]);
                      }
                    }, function(error) {
                      $log.error('Error occured while requesting data: ', error);
                    });
                  }
                }
              });
          }
        }

        function localeSensitiveComparator(v1, v2) {
          // If we don't get strings, just compare by index
          if (v1.type !== 'string' || v2.type !== 'string') {
            return (v1.index < v2.index) ? -1 : 1;
          }

          // Compare strings alphabetically, taking locale into account
          return v1.value.localeCompare(v2.value);
        }

        //If is a enumeration, we're gonna search the items into the schema
        if (scope.dynamicFieldConfiguration.dataType === 'enumeration' || scope.dynamicFieldConfiguration.dataType === 'xs:enumeration') {
          $http.get(scope.dynamicFieldConfiguration.schema.href)
            .then(function(response) {
              var items = _.get(response.data, 'results[0].restrictions[0].enumeration.item', []).filter(function(item) {
                return item.actif === true;
              });
              if(ordreOptions) {
                items = $filter('orderBy')(items, ordreOptions, false, localeSensitiveComparator);
              }
              _.set(scope, 'dynamicFieldConfiguration.restrictions[0].enumeration.item', items);
            });
        }

        // Check if we have access to property "scopeFields" of scope of the directive "dynamic-form-groups"
        if (scopeFields) {
          _.each(properties, function(property) {
            // Evaluate property
            if (formUtils.checkIsAngularExpression(scope.configuration[property])) {
              watches.push(scopeFields.$watch(scope.configuration[property], function(newValue, oldValue) {
                if (newValue !== undefined) {
                  scope.dynamicFieldConfiguration[property] = newValue;
                }
                // Force clear field model on change on hidden property
                if (property === 'hidden' && newValue !== oldValue) {
                  scope.model = undefined;
                }
              }));
            }
          });
        }

        /**
         * When displayKey contains several keys separated by a comma, returns the first key
         * @param  {string} displayKey param from configuration
         * @return {string}            First displayKey param if several
         */
        scope.getFirstDisplayKey = function(displayKey) {
          // Updates displayKey scope's variable used in other functions
          scope.displayKey = displayKey;

          return displayKey && displayKey.indexOf(',') !== -1 ?
            displayKey.substring(0, displayKey.indexOf(',')) :
            displayKey;
        };

        /**
         * Function executed while the typeahead value is selected. This was added in order to update the tooltip
         * located near the typeahead when a new value is selected.
         *
         * @param  {[type]} $item  [description]
         * @param  {[type]} $model [description]
         * @param  {[type]} $label [description]
         * @return {[type]}        [description]
         */
        scope.typeaheadOnSelect = function($item) {
          if ($item &&
            _.get(scope, 'displayKey') &&
            scope.displayKey.indexOf(',') !== -1 &&
            scope.dynamicFieldConfiguration.helpTooltip) {

            // Backup help text when memo mode is activated on typeahead
            if (_.isEmpty(scope.helpViewConfigurationBackup)) {
              scope.helpViewConfigurationBackup = angular.copy(scope.dynamicFieldConfiguration.help);
            }
            scope.dynamicFieldConfiguration.help = $translate.instant(scope.helpViewConfigurationBackup, {
              data: $item
            });
          }
        };

        /**
         * Function executed when the user types in the typeahead
         * @return {[type]} [description]
         */
        scope.onChange = function() {
          // Backup help text when memo mode is activated on typeahead
          if (_.isEmpty(scope.helpViewConfigurationBackup)) {
            scope.helpViewConfigurationBackup = angular.copy(scope.dynamicFieldConfiguration.help);
          }
          scope.dynamicFieldConfiguration.help = ' ';
        };

        // Access to regex comparison from template
        scope.regTest = function(value, regexp) {
          return new RegExp(regexp).test(value);
        };

        scope.dynamicSearch = function dynamicSearch(searchKey) {
          if (searchKey !== undefined && searchKey !== '') {
            // Double the special characters
            searchKey = searchKey.replace(/([.*+?^=!:${}()|\'\"\[\]\/\\])/g, '$1$1');
            var filter = 'substringof(\'' + searchKey.toLowerCase() + '\', tolower(' + scope.dataSchemaConfiguration.searchKey + '))';

            var options = {
              url: scope.source,
              params: {
                $filter: filter,
                $inlinecount: 'allpages',
                $select: scope.dataSchemaConfiguration.displayKey,
                $orderby: scope.dataSchemaConfiguration.displayKey + ' asc',
                $top: 200
              }
            };

            if (scope.dataSchemaConfiguration.authSource === true && scope.authUser && scope.authPass) {
              var authorization = 'Basic ' + btoa(scope.authUser + ':' + scope.authPass);
              _.set(options, 'headers.Authorization', authorization);
            }

            var firstDisplayKey = scope.getFirstDisplayKey(scope.dataSchemaConfiguration.displayKey);
            return $http(options)
              .then(function(response) {

                scope.dataFetchedFromServer = true;
                var items = _.get(response.data, 'd.results', []);
                return _.uniq(items, firstDisplayKey);
              }, function(error) {
                scope.dataFetchedFromServer = true;
                $log.error('An error occured while requesting data from dynamic-field: ', error);
              });
          }
        };

        scope.isRequired = function() {
          return (
            // ancienne condition (compatibilité ascendante)
            (!scope.remoteValidation &&
              !scope.configuration.hidden &&
              scope.configuration.required) ||
            // nouvelle condition (bug fix)
            (!scope.remoteValidation &&
              !scope.dynamicFieldConfiguration.hidden &&
              scope.dynamicFieldConfiguration.required)
          );
        };

        scope.$on('$destroy', function() {
          _.each(watches, function(watch) {
            watch();
          });
        });
      }
    };
  }
]);

/**
 * @ngdoc directive
 * @area api
 * @module form.directives
 * @name dynamic-form-groups
 * @scope
 * @restrict EA
 * @description
 *
 *   A dynamic form whose presentation will describe a simple list of groups of fields.
 *
 * @param {object} data - The object that will be completed by this dynamic form.
 * @param {object} presentation - The presentation used to render the form. TODO detailed description of format when matured.
 * @param {object} viewConfiguration - A view configuration as returned by the resource-manager service for example
 *
 * @example
 *
 *   `<dynamic-form-groups data="demandeur.specificData" viewConfiguration="teleserviceConfiguration" presentation="formGroupPresentation"></dynamic-form-groups>`
 *
 */

angular.module('form.directives').directive('dynamicFormGroups', ['$translate', 'formUtils',
  function($translate, formUtils) {
    'use strict';

    /**
     * Define a new scope for fields for analyse angular expression use by some properties
     * @param  {object} scope  Scope of the directive
     * @param  {object} fields List of fields use by this dynamic form
     * @return {object}        Specific scope for analyse expression
     */
    var getScopeFields = function(scope) {
      var scopeFields = scope.$new();
      _.each(scope.fields, function(field, key) {
        scopeFields[key] = field;
      });

      return scopeFields;
    };

    /**
     * Define translations for some properties
     * @param  {array} properties Properties
     * @param  {string} namespace  Namespace
     * @param  {object} group      Group's presentation
     */
    var defineTranslations = function(properties, namespace, group) {
      _.each(properties, function(element) {

        // Key for the translation
        var key = namespace + '.group.' + group.id + '.' + element;
        group[element] = $translate.instant(key);

        if (group[element] === key) {
          group[element] = null;
        }
      });
    };

    return {
      restrict: 'EA',
      replace: true,
      templateUrl: 'form/form-directives/dynamic-form-groups/dynamic-form-groups.html',
      require: '^form',
      scope: {
        data: '=',
        presentation: '=',
        namespace: '=',
        fields: '=',
        mdm: '=',
        readOnly: '=',
        authUser: '=',
        authPass: '=',
        viewConfiguration: '='
      },
      controller: ['$scope', function($scope) {

        var that = this;

        // Creation of a new scope for analyse angular expressions for properties
        // Given access to this property of scope to another directives, like 'dynamic-field'
        that.scopeFields = getScopeFields($scope);

        // Watch entire data object who contains value of each field.
        // We update the property 'scopeFields' use by differents watcher
        $scope.$watch('data', function(newValue) {
          if (newValue) {
            _.each($scope.data, function(element, key) {
              _.set(that, 'scopeFields.' + key + '.value', $scope.data[key]);
            });
          }
        }, true);

        // Presentation
        if ($scope.presentation && $scope.presentation.groups) {

          // Iteration on each group of the presentation
          _.each($scope.presentation.groups, function(group) {

            // Manage translations for this properties
            var properties = ['legend', 'header', 'footer'];
            defineTranslations(properties, $scope.namespace, group);

            // Evaluation of angular expression on group's value property 'visible'
            // We must clone the object for don't loose angular expression define on the property.
            var groupConfiguration = angular.copy(group);
            group.configuration = groupConfiguration;

            if (formUtils.checkIsAngularExpression(group.hidden)) {
              that.scopeFields.$watch(groupConfiguration.hidden, function(newValue) {
                if (newValue !== undefined) {
                  groupConfiguration.hidden = newValue;
                  if (groupConfiguration.hidden) {
                    _.each(group.fields, function(field) {
                      if ($scope.data[field]) {
                        $scope.data[field] = undefined;
                      }
                    });
                  }
                }
              });
            } else {
              groupConfiguration.hidden = false;
            }
          });
        }
      }]
    };
  }
]);

/**
 * @ngdoc directive
 * @area api
 * @module form.directives
 * @name dynamic-name
 * @scope
 * @restrict EA
 * @description
 *
 *   Workaround to allow dynamic name on form elements.
 *
 *   See http://stackoverflow.com/questions/14378401/dynamic-validation-and-name-in-a-form-with-angularjs
 *
 * @param {string} dynamicName - An expression to evaluate and assign as the input's name
 *
 * @example
 *
 *   `<input type="text" dynamic-name="myFieldDefinition.name">`
 *
 */ 

angular.module('form.directives').directive('dynamicName', ['$compile', '$parse', 
  function($compile, $parse) {
    'use strict';
    return {
      restrict: 'A',
      terminal: true,
      priority: 100000,
      link: function(scope, elem) {
        var name = $parse(elem.attr('dynamic-name'))(scope);
        elem.removeAttr('dynamic-name');
        elem.attr('name', name);
        if (!elem.attr('id')) {
          elem.attr('id', name);
        }
        $compile(elem)(scope);
      }
    };
  }]);
angular.module('form.directives').directive('editableGrid', ['gridUtils',

  function(gridUtils) {
    'use strict';

    return {
      restrict: 'EA',
      replace: true,
      transclude: true,
      templateUrl: 'form/form-directives/editable-grid/editable-grid.html',
      scope: {
        viewConfiguration: '=',
        config: '=configuration',
        ns: '=namespace',
        model: '=',
        gridOptions: '='
      },
      link: function(scope, element, attrs, ctr, transclude) {
        scope.configuration = scope.config || _.get(scope, 'viewConfiguration.fields.' + scope.name) || {};
        scope.namespace = scope.ns || scope.configuration.ns || _.get(scope, 'viewConfiguration.ns');

        _.each(scope.gridOptions.columns, function(column) {
          if (column.editorTemplate) {
            column.editor = gridUtils.fieldEditor({
              scope: scope,
              template: column.editorTemplate,
              transclude: transclude
            });
          }
          if (column.editorTemplateUrl) {
            column.editor = gridUtils.fieldEditor({
              scope: scope,
              templateUrl: column.editorTemplateUrl,
              transclude: transclude
            });
          }
        });
      }
    };
  }
]);

/**
 * @ngdoc directive
 * @area api
 * @module form.directives
 * @name exerice-field
 * @scope
 * @restrict EA
 * @description
 *
 *   A basic exercice field for dynamic forms.
 *
 *   Uses validation-field directive for automatic layout, configuration and labels management.
 *
 *   Uses kendo-ui datePicker directive
 *
 * @param {string} name - The name of the field in the current form and in the model
 * @param {object} viewConfiguration - A view configuration as returned by the resource-manager service for example
 * @param {object} configuration - The configuration of the field extracted from a viewConfiguration object (in replacement to viewConfiguration)
 * @param {string} namespace - The configuration of the field extracted from a viewConfiguration object (in replacement to viewConfiguration)
 * @param {String} model - The model to bind to
 * @param {boolean} bare - Specify that the field should be left alone without label and help messages
 *
 * @example
 *
 *   `<exerice-field name="dateNaissance" view-configuration="identificationDemandeurConfiguration" data="demandeur"></exerice-field>`
 *
 */

angular.module('form.directives').directive('exerciceField', ['dateUtils', '$timeout', '$translate',

  function(dateUtils, $timeout, $translate) {
    'use strict';

    return {
      restrict: 'EA',
      replace: true,
      templateUrl: 'form/form-directives/exercice-field/exercice-field.html',
      scope: {
        name: '@',
        viewConfiguration: '=',
        config: '=configuration',
        ns: '=namespace',
        model: '=',
        bare: '=',
        readOnly: '=',
        remoteValidation: '=?',
        labelClass: '=?'
      },
      link: function(scope) {

        var watches = [];
        var timer;
        scope.forceRefresh = true;

        // https://jira.mgdis.fr/browse/PLAID-10886: on enlève la valeur par défaut initialisé à l'année courante...
        // // Set default date to today if no model defined to avoid "Invalid Date" value
        // // https://jira.mgdis.fr/browse/PLAID-3593
        // if (scope.model === null || scope.model === undefined) {
        // 	scope.model = new Date().getFullYear();
        // }
        scope.exerciceField = {
          ngModel: scope.model,
          datePickerConfig: {
            value: new Date(scope.model, 0, 1),
            format: 'yyyy',
            culture: 'fr-FR',
            start: 'decade',
            depth: 'decade'
          }
        };

        // https://jira.mgdis.fr/browse/PLAID-10886: ... et on supprime la valeur du datePickerConfig inexistante
        if (_.isEmpty(scope.model)) {
          delete scope.exerciceField.datePickerConfig.value;
        }

        // Set configuration
        watches.push(scope.$watch('[config, viewConfiguration]', function() {
					
          //met un min et un max en place
          //on veut une date au format yyyy
          scope.configuration = scope.config || _.get(scope, 'viewConfiguration.fields.' + scope.name) || {};
					
          var findMin = _.find(_.get(scope, 'configuration.restrictions'), function(contrainte) {
            if (_.get(contrainte, 'minInclusive')) {
              return contrainte;
            }
          });
          var findMax = _.find(_.get(scope, 'configuration.restrictions'), function(contrainte) {
            if (_.get(contrainte, 'maxInclusive')) {
              return contrainte;
            }
          });
          var sizeRestrictions = 0;
          if (_.get(scope, 'configuration.restrictions') && _.get(scope, 'configuration.restrictions').length>=1) {
            sizeRestrictions = scope.configuration.restrictions.length-1;
          }
          _.set(scope, 'configuration.restrictions.'+ sizeRestrictions +'.minInclusive', _.get(findMin, 'minInclusive', 1000));
          _.set(scope, 'configuration.restrictions.'+ sizeRestrictions +'.maxInclusive', _.get(findMax, 'maxInclusive', 9999));

          scope.namespace = scope.ns || scope.configuration.ns || _.get(scope, 'viewConfiguration.ns');

          // Initialize (or reinitialize min and max)
          delete scope.exerciceField.datePickerConfig.min;
          delete scope.exerciceField.datePickerConfig.max;
          dateUtils.setMinRestriction(scope.configuration.restrictions, scope.exerciceField, 'years');
          dateUtils.setMaxRestriction(scope.configuration.restrictions, scope.exerciceField, 'years');

          // dirty dirty ! force refreshing of the kendo-ui directive so that configuration modifications are applied.
          scope.forceRefresh = false;
          timer = $timeout(function() {
            scope.forceRefresh = true;
            scope.$digest();
          }, 1);

          // Read remote validation from directive, configuration or viewConfiguration
          scope.remoteValidation = scope.remoteValidation !== undefined ? scope.remoteValidation :
            scope.configuration.remoteValidation !== undefined ? scope.configuration.remoteValidation :
              _.get(scope, 'viewConfiguration.remoteValidation') !== undefined ? _.get(scope, 'viewConfiguration.remoteValidation') : false;
        }, true));

        // Mise à jour de la valeur de l'exercice si la modification du model
        // se fait depuis l'extérieur de la directive
        watches.push(scope.$watch('model', function(newValue, oldValue) {
          if(newValue !== oldValue){
            scope.exerciceField.ngModel = scope.model;
          }
        }));

        // Binding from input to model
        watches.push(scope.$watch('exerciceField.ngModel', function() {
          scope.model = scope.exerciceField.ngModel ? _.parseInt(scope.exerciceField.ngModel) : undefined;
        }));

        scope.getPlaceholder = function() {
          var placeholderValue = _.get(scope, 'configuration.placeholder');
          if (!placeholderValue) {
            var placeholderKey = scope.namespace + '.' + scope.name + '.placeholder';
	          placeholderValue = $translate.instant(placeholderKey) !== placeholderKey ? $translate.instant(placeholderKey) : '';
	        }
          if (placeholderValue && placeholderValue === 'currentYear') {
            var currentYear = new Date();
            return currentYear.getFullYear();
          } else {
            return placeholderValue;
          }
        };

        scope.validateExerciceField = function() {
          // Case where the field was modified but not its model, it means that kendo rejected its format
          // if (scope.exerciceField.ngModel && !scope.exerciceField.kModel) {
          // 	return false;
          // }
          // Case where the value doesn't respect min and max constraints. Possible if the user doesn't use the datePicker.
          if (scope.exerciceField.datePickerConfig.max && scope.model > scope.exerciceField.datePickerConfig.max.getFullYear()) {
            return false;
          }
          if (scope.exerciceField.datePickerConfig.min && scope.model < scope.exerciceField.datePickerConfig.min.getFullYear()) {
            return false;
          }
          if (scope.exerciceField.ngModel && !new RegExp(/^[0-9]*$/).test(scope.exerciceField.ngModel)) {
            return false;
          }
          return true;
        };

        scope.$on('$destroy', function() {
          $timeout.cancel(timer);
          _.each(watches, function (watch) {
            watch();
          });

        });

      }
    };
  }
]);

/**
 * @ngdoc directive
 * @area api
 * @module form.directives
 * @name file-field
 * @scope
 * @restrict EA
 * @description
 *
 *   A basic file field, usable for dynamic forms. The binded data is an array of files.
 *
 *   Uses validation-field directive for automatic layout, configuration and labels management.
 *
 * @param {string} name - The name of the field in the current form and in the model
 * @param {object} viewConfiguration - A view configuration as returned by the resource-manager service for example
 * @param {object} configuration - The configuration of the field extracted from a viewConfiguration object (in replacement to viewConfiguration)
 * @param {string} namespace - The configuration of the field extracted from a viewConfiguration object (in replacement to viewConfiguration)
 * @param {String} model - The model to bind to
 * @param {boolean} bare - Specify that the field should be left alone without label and help messages
 *
 * @example
 *
 *   `<file-field id="file1" name="pieces" model="message.pieces" view-configuration='contactConfiguration'></file-field>`
 *
 */
angular.module('form.directives').directive('fileField', function() {
  'use strict';

  return {
    restrict: 'EA',
    replace: false,
    templateUrl: 'form/form-directives/file-field/file-field.html',
    scope: {
      model: '=',
      config:'=configuration',
      ns: '=namespace',
      viewConfiguration: '=',
      name: '@',
      bare: '=',
      remoteValidation: '=?'
    },
    link: function(scope) {
      scope.configuration = scope.config || _.get(scope, 'viewConfiguration.fields.' + scope.name) || {};
      scope.namespace = scope.ns || _.get(scope, 'viewConfiguration.ns');
      scope.required = scope.configuration.required;  // Needed for validation-field

      // Read remote validation from directive, configuration or viewConfiguration
      scope.remoteValidation = scope.remoteValidation !== undefined ? scope.remoteValidation :
        scope.configuration.remoteValidation !== undefined ? scope.configuration.remoteValidation :
          _.get(scope, 'viewConfiguration.remoteValidation') !== undefined ? _.get(scope, 'viewConfiguration.remoteValidation') : false;

      scope.model = scope.model || [];
    }
  };
});
/**
 * @ngdoc directive
 * @area api
 * @module form.directives
 * @name file-input
 * @scope
 * @restrict EA
 * @description
 *
 *   A basic file input. For dynamic forms, please use file-field. The binded data is an array of files.
 *
 *   Uses bootstrap to display an input in adequation with the style, and allows to configure the button's label.
 *
 * @param {string} name - The name of the field in the current form and in the model
 * @param {String} model - The model to bind to
 * @param {string} label - Button's label
 * @param {string} placeholder - Text displayed when no file is selected
 * @param {boolean} required
 * @param {boolean} multiple - Wether the file-input can upload several files or not
 * @param {string} extensionsAllowed - list of extensions allowed
 * @param {string} fileSizeLimit - size limit for each input file
 * @param {string} maxFiles - maximum number of files allowed
 *
 * @example
 *
 *   `<file-input
 *      name="{{name}}"
 *      model="model"
 *      label="{{namespace + '.' + name + '.button' | translate}}"
 *      placeholder="{{field.placeholder}}"
 *      required="field.required"
 *      multiple="field.multiple"
 *      extensionsAllowed=['pdf', 'png']
 *      fileSizeLimit="10485760"
 *      maxFiles="2">
 *    </file-input>`
 *
 */
angular.module('form.directives').directive('fileInput', ['$translate', function($translate) {
  'use strict';

  return {
    restrict: 'EA',
    replace: false,
    templateUrl: 'form/form-directives/file-field/file-input.html',
    scope: {
      model: '=',
      required: '=',
      multiple: '=',
      label: '@',
      name: '@',
      placeholder: '@',
      extensionsAllowed: '=',
      fileSizeLimit: '=',
      maxFiles: '=',
    },
    link: function(scope, element) {
      var watches = [];
      scope.model = scope.model || [];

      // File input are one of the few elements angular doesn't manage well yet, so there's going to be some DOM manipulations
      var input = element.find('input[type="file"]');

      // We have to add the multiple attribute manually, since the input checks only its presence (even with a value of "false")
      watches.push(scope.$watch('multiple', function(active) {
        if (active === true) {
          input.attr('multiple', '');
        }
      }));

      watches.push(scope.$watch('extensionsAllowed', function(extensionsAllowed) {
        if (extensionsAllowed) {
          input.attr('accept', extensionsAllowed.map(function(extension) {
            return '.' + extension;
          }).join(','));
        }
      }));

      // When files are added, we update the model
      input.on('change', function() {
        scope.fileErrors = undefined;
        scope.$apply(function() {
          // Creating a new instance of array would break the binding, so we change the current one
          scope.model.splice(0);
          // Cette ligne ne fonctionne pas sous ie en version 3 de lodash mais c'est corrigé en 4.
          // _.merge(scope.model,element.find('input')[0].files);
          var files = element.find('input')[0].files;

          if(scope.maxFiles && files.length > scope.maxFiles) {
            scope.fileErrors = $translate.instant('form.field.error.file.maxFilesExceeded', {maxFiles: scope.maxFiles});
          }

          files.forEach(function(file) {
            if(scope.extensionsAllowed && !scope.extensionsAllowed.includes(file.name.split('.').pop())) {
              scope.fileErrors = $translate.instant('form.field.error.file.extensionUnauthorized', {
                extensions: scope.extensionsAllowed
              });
            }

            if(scope.fileSizeLimit) {
              if(file.size > scope.fileSizeLimit) {
                scope.fileErrors = $translate.instant('form.field.error.file.fileSizeLimitExceeded', {
                  fileSizeLimit: scope.fileSizeLimit / 1024 / 1024
                });
              }
            }
          })

          if(!scope.fileErrors) {
            Array.prototype.push.apply(scope.model, files);
          }

          // Displaying a list of the registered files ; This list is also used to check the presence of data when the field is required
          scope.names = _.map(scope.model, 'name').join(', ');
        });
      });

      // Note: since the objects inside model are Files taken directly from the DOM, we can't watch them directly
      watches.push(scope.$watch('model.length', function() {
        // Empty names if the model is initialized from the outside
        if (scope.model.length === 0) {
          scope.names = '';
        }
      }));

      scope.$on('$destroy', function() {

        input.off();
        _.each(watches,function (watch) {
          watch();
        });
      });
    }
  };
}]);
/**
 * @ngdoc directive
 * @area api
 * @module form.directives
 * @name format-field
 * @scope
 * @restrict A
 * @description
 *
 *    Format a field in realtime while it is typed.
 *
 * @param {string} format - The key of the format to apply. TODO detailed list of available formts.
 *
 * @example
 *
 *   `<input type="text" format-field format="'phone'">`
 *
 */

angular.module('form.directives').directive('formatField', ['$filter', 'formFormat',

  function($filter, formFormat) {
    'use strict';
    return {
      require: 'ngModel',
      scope: {
        format: '='
      },
      link: function(scope, element, attrs, model) {

        var formatField = function() {
          // Type du format
          // Passage en minuscule pour gérer le format associé directement
          // au niveau du champ où les valeurs du format sont en majuscule
          var format = _.get(scope, 'format', '').toLowerCase();
          switch (format) {
            case 'lowercase':
            case 'uppercase':
              model.$setViewValue($filter(format)(element.val()));
              break;
            case 'capitalize':
            case 'capitalizefirst':
              model.$setViewValue(formFormat.capitalizeFirst(element.val()));
              break;
            case 'phone':
              model.$setViewValue(formFormat.formatPhone(element.val()));
              break;
          }
        };

        scope.blur = function() {
          formatField();
          model.$render();
        };

        scope.keydownPress = function(event) {
          if (event.which === 13) {
            formatField();
            model.$render();
          }
        };

        // Format the field when we leave this field
        // When we call "model.$setViewValue" we update the model and when we call "model.$render" we update the input field
        element.bind('blur', scope.blur);

        // Format the field when we type on the enter touch
        element.bind('keydown keypress', scope.keydownPress);

        scope.$on('$destroy', function() {
          element.unbind('blur', scope.blur);
          // Format the field when we type on the enter touch
          element.unbind('keydown keypress', scope.keydownPress);
        });
      }

    };
  }
]);

/* global kendo */

/**
 * @ngdoc directive
 * @area api
 * @module form.directives
 * @name number-field
 * @scope
 * @restrict EA
 * @description
 *
 *   A basic number field for dynamic forms.
 *
 *   Uses validation-field directive for automatic layout, configuration and labels management.
 *
 *   Uses kendo-ui numeric textbox directive
 *
 * @param {string} name - The name of the field in the current form and in the model
 * @param {object} viewConfiguration - A view configuration as returned by the resource-manager service for example
 * @param {object} configuration - The configuration of the field extracted from a viewConfiguration object (in replacement to viewConfiguration)
 * @param {string} namespace - The configuration of the field extracted from a viewConfiguration object (in replacement to viewConfiguration)
 * @param {String} model - The model to bind to
 * @param {number} [subdigits] - The number of subdigits supported (0 for an integer, usually 2 for a float). Default to 2.
 * @param {string} [type] - The type of number, 'n' for number, 'c' for currency, 'p' for percentage. Default to 'n'.
 * @param {boolean} bare - Specify that the field should be left alone without label and help messages
 *
 * @example
 *
 *   `<number-field name="montant" view-configuration="infosGeneralesConfiguration" data="demande" subdigits="2" type="c"></number-field>`
 *
 */
angular.module('form.directives').directive('numberField', ['$translate',

  function($translate) {
    'use strict';

    return {
      restrict: 'EA',
      replace: true,
      templateUrl: 'form/form-directives/number-field/number-field.html',
      scope: {
        name: '@',
        viewConfiguration: '=',
        config: '=configuration',
        ns: '=namespace',
        model: '=',
        subdigits: '@',
        type: '@', // n for number, c for currency, p for percentage
        bare: '=',
        readOnly: '=',
        labelWidth: '=',
        fieldWidth: '=',
        helpWidth: '=',
        remoteValidation: '=?',
        labelClass: '=?',
        defaultToZero: '=?'
      },
      link: function(scope) {
        scope.configuration = scope.config || _.get(scope, 'viewConfiguration.fields.' + scope.name) || {};
        scope.namespace = scope.ns || scope.configuration.ns || _.get(scope, 'viewConfiguration.ns');

        // Read remote validation from directive, configuration or viewConfiguration
        scope.remoteValidation = scope.remoteValidation !== undefined ? scope.remoteValidation :
          scope.configuration.remoteValidation !== undefined ? scope.configuration.remoteValidation :
            _.get(scope, 'viewConfiguration.remoteValidation') !== undefined ? _.get(scope, 'viewConfiguration.remoteValidation') : false;

        var watches = [];
        scope.subdigits = scope.subdigits || 2;
        if (scope.defaultToZero === undefined) {
          scope.defaultToZero = true;
        }
        scope.type = scope.type || 'n';
        scope.format = scope.type + scope.subdigits;
        scope.configuration.type = 'number';

        // Initialize display value when empty to avoid hyphen double inputs for negative numbers
        scope.numberField = {
          displayValue: scope.model || 0,
          modelValue: scope.model,
          numericTextBoxConfig: {
            format: scope.format,
            spinners: scope.configuration.spinners || false
          },
          decimals: parseInt(scope.subdigits)
        };

        // Little manipulation so that we can add placeholder
        scope.buildKOptions = function() {
          var placeholderValue = _.get(scope, 'configuration.placeholder');
          if (!placeholderValue) {
            var placeholderKey = scope.namespace + '.' + scope.name + '.placeholder';
            placeholderValue = $translate.instant(placeholderKey) !== placeholderKey ? $translate.instant(placeholderKey) : '';
          }
          scope.numberField.numericTextBoxConfig.placeholder = placeholderValue;
          return scope.numberField.numericTextBoxConfig;
        };

        scope.getMinlength = function(fieldMinlength) {
          if (fieldMinlength !== undefined) {
            // replace string with int
            return typeof fieldMinlength === 'string' ? parseInt(fieldMinlength) : fieldMinlength;
          } else {
            return 0;
          }
        };

        scope.getMaxlength = function(fieldMaxlength) {
          if (fieldMaxlength !== undefined) {
            return typeof fieldMaxlength === 'string' ? parseInt(fieldMaxlength) : fieldMaxlength;
          } else {
            if (scope.subdigits !== undefined && scope.subdigits !== '0') {
              var res = 14 + Number(scope.subdigits);
              return res;
            } else {
              return 13;
            }
          }
        };

        /*
         * This trigger is important
         *
         * It will catch all the change of the displayed value (the value that is changed while typing)
         * And it will forbid to type more than the allowed number of decimals and set the value to the modelValue
         */
        watches.push(scope.$watch('numberField.displayValue', function(newValue, oldValue) {
          if (newValue !== oldValue) {
            if (scope.defaultToZero && (newValue === '')) {
              //If is empty, asign now...
              scope.numberField.modelValue = 0;
            } else {
              // Check the decimals rounding to the max
              if (scope.subdigits > 0 && kendo.parseFloat(scope.numberField.displayValue) !== null) {
                var normalizedStringValue = kendo.parseFloat(scope.numberField.displayValue).toString();
                var indexOfPoint = normalizedStringValue.indexOf('.');
                if (indexOfPoint > 0) {
                  scope.numberField.displayValue = normalizedStringValue.substring(0, indexOfPoint + 1 + parseInt(scope.subdigits));
                }
                scope.numberField.modelValue = kendo.parseFloat(scope.numberField.displayValue);
              }
              // Else, simply parse to an integer to suppress the possible unexpected '0'
              else {
                scope.numberField.modelValue = parseInt(scope.numberField.displayValue);
              }
            }
          }

        }));

        // We have to explicitly two way binding the value with parent model
        watches.push(scope.$watch('numberField.modelValue', function(newValue) {
          scope.model = !(_.isNull(newValue) || _.isNaN(newValue)) ? newValue : undefined;
        }));

        watches.push(scope.$watch('model', function(newValue) {
          scope.numberField.modelValue = newValue !== undefined ? newValue : scope.numberField.modelValue;
          scope.numberField.displayValue = newValue;
        }));

        // Process restrictions
        if(scope.configuration && scope.configuration.restrictions && Array.isArray(scope.configuration.restrictions) && scope.configuration.restrictions.length > 0) {
         
          scope.configuration.restrictions.forEach(function(element){
            // Set min and max
            if(element.hasOwnProperty('minInclusive')) {
              scope.numberField.numericTextBoxConfig.min = element.minInclusive;
            }
            if(element.hasOwnProperty('maxInclusive')) {
              scope.numberField.numericTextBoxConfig.max = element.maxInclusive;
            }
            if(element.hasOwnProperty('minExclusive')) {
              scope.numberField.numericTextBoxConfig.min = (element.minExclusive - Math.pow(10, scope.subdigits * -1));
            }
            if(element.hasOwnProperty('maxExclusive')) {
              scope.numberField.numericTextBoxConfig.max = (element.maxExclusive - Math.pow(10, scope.subdigits * -1));
            }
          });
        }

        scope.$on('$destroy', function() {

          _.each(watches, function(watch) {
            watch();
          });
        });
      }
    };
  }
]);

/**
 * @ngdoc directive
 * @area api
 * @module form.directives
 * @name options-attributes
 * @require select
 * @restrict A
 * @description
 *
 *   Allow to add attributes to options in a select using ng-options
 *   Attributes are taken from the array on which ng-options is based. They are distributed using the array's indexes.
 *   However, if a "orderBy" or "groupBy" is used, it is advised to use "track by" to put the corresponding attributes to the right option.
 *
 * @param {object} optionsAttributes - object associating attributes to add with path to property in data
 *
 * @example
 *
 *   `<select name="city"
 *     ng-options="city as city.expand.label for city in listCities track by city.reference"
 *     options-attributes="{title: 'expand.label'}"></select>`
 */
angular.module('form.directives').directive('optionsAttributes', ['$timeout', function($timeout) {
  'use strict';

  return {
    restrict: 'A',
    replace: false,
    require: 'select',
    link: function(scope, element, attributes) {
      // Read parameters
      var attributesMap = scope.$eval(attributes.optionsAttributes);

      // Throw an error if the ngOptions attribute isn't defined
      if (!attributes.ngOptions) {
        throw Error('options-attributes used without ng-options');
      }
      // Read ng-options attribute to retrieve data to use
      var dataName = attributes.ngOptions.match(/for .+ in ([^ ^|]+)/)[1];
      var trackByMatches = attributes.ngOptions.match(/track by ([^ ^|]+)/);
      var trackBy = trackByMatches && trackByMatches[1] ? trackByMatches[1].split('.').slice(1).join('.') : '';

      scope.$watch(dataName, function(data) {
        if (data && attributesMap) {
          // Attributes must be added only once option elements are generated
          $timeout(function() {
            // Iterate only over non-empty option elements
            var optionsElements = _.filter($('option', element), function(el) {
              return !!$(el).attr('value');
            });

            _.each(optionsElements, function(el, i) {
              // The data used is associated with index by default
              var dataItem = data[i];
              if (trackBy) {
                // Otherwise, we find the corresponding data by comparing the property given in trackBy with the option's value
                dataItem = _.find(data, function(item) {
                  return _.get(item, trackBy) === $(el).attr('value');
                });
              }

              // Write attributes on the element, with selected data
              _.forOwn(attributesMap, function(key, attribute) {
                $(el).attr(attribute, _.get(dataItem, key));
              });
            });
          }, 0);
        }
      });
    }
  };
}]);

/**
 * @ngdoc directive
 * @area api
 * @module form.directives
 * @name select-field
 * @scope
 * @restrict EA
 * @description
 *
 *   A field dedicated to selected a value from a list
 *
 *   Uses validation-field directive for automatic layout, configuration and labels management.
 *
 * @param {string} name - The name of the field in the current form and in the model
 * @param {object} viewConfiguration - A view configuration as returned by the resource-manager service for example
 * @param {object} configuration - The configuration of the field extracted from a viewConfiguration object (in replacement to viewConfiguration)
 * @param {string} namespace - The configuration of the field extracted from a viewConfiguration object (in replacement to viewConfiguration)
 * @param {String} model - The model to bind to
 * @param {Object} source - An object as returned by the mdm service for the a source
 * @param {boolean} bare - Specify that the field should be left alone without label and help messages
 * @param {boolean} limit - Specify a limit to the number of elements to display (only with typeahead type)
 *
 * @example
 *
 *   `<select-field name="naf" view-configuration="identificationDemandeurConfiguration" data="demandeur" source="mdm.naf"></select-field>`
 *
 */

angular.module('form.directives').directive('selectField', ['$timeout', 'mdmService', 'mdmUtils', 'formUtils', '$filter',

  function($timeout, mdmService, mdmUtils, formUtils, $filter) {
    'use strict';

    return {
      restrict: 'EA',
      replace: true,
      templateUrl: 'form/form-directives/select-field/select-field.html',
      scope: {
        name: '@',
        viewConfiguration: '=',
        config: '=configuration',
        ns: '=namespace',
        model: '=',
        source: '=',
        type: '@',
        bare: '=',
        limit: '=?',
        tooltips: '=?',
        readOnly: '=',
        labelWidth: '=',
        fieldWidth: '=',
        helpWidth: '=',
        onChange: '&?',
        remoteValidation: '=?',
        labelClass: '=?',
        defaultValueUndefined: '<'
      },
      link: function(scope) {
        scope.configuration = scope.config || _.get(scope, 'viewConfiguration.fields.' + scope.name) || {};
        scope.namespace = scope.ns || scope.configuration.ns || _.get(scope, 'viewConfiguration.ns');
        scope.limit = scope.limit || 20;

        // ng-options order
        var ordreOptions = _.get(scope.configuration, 'select.ordreOptions') || _.get(scope.viewConfiguration, 'select.ordreOptions');
        if(ordreOptions && _.get(scope, 'source.array')) {
          if(!_.startsWith(ordreOptions, 'expand')) {
            ordreOptions = 'expand.' + ordreOptions;
          }
          scope.source.array = $filter('orderBy')(scope.source.array, ordreOptions);
        }

        // Read remote validation from directive, configuration or viewConfiguration
        scope.remoteValidation = scope.remoteValidation !== undefined ? scope.remoteValidation :
          scope.configuration.remoteValidation !== undefined ? scope.configuration.remoteValidation :
            _.get(scope, 'viewConfiguration.remoteValidation') !== undefined ? _.get(scope, 'viewConfiguration.remoteValidation') : false;

        scope.configuration.loading = true;

        // Displays or not tooltips
        scope.tooltips = !!(scope.tooltips || scope.configuration.tooltips);

        // La source de données est une url
        if (_.get(scope.configuration, 'restrictions[0].dataURL')) {
          var keys = [scope.configuration.restrictions[0].dataURL];
          var urlSource = scope.configuration.restrictions[0].dataURL;
          mdmService.fill(keys, urlSource).then(function(result) {
            scope.source = result[scope.configuration.restrictions[0].dataURL];
            if(ordreOptions && scope.source.array) {
              scope.source.array = $filter('orderBy')(scope.source.array, ordreOptions);
            }
          });
        }

        // manage expand of specifics values
        if (scope.model && scope.model.expand && scope.model.expand.d) {
          scope.model.expand = scope.model.expand.d;
        }

        scope.$watch('source', function(newVal) {
          if (newVal) {
            var newModelValue = mdmUtils.refOrEntry(scope.source, scope.model);

            // if the binding "default-value-undefined" is set use undefined instead of null
            // (binding set to true to avoid contract error on save)
            if (newModelValue !== null || !scope.defaultValueUndefined) {
              scope.model = newModelValue;
            } else {
              scope.model = undefined;
            }

            scope.configuration.loading = false;
          }
        });

        formUtils.wrapPrimitiveInObject(scope, 'model', 'data');

        // Callback on changes
        scope.change = function() {
          if (scope.onChange) {
            // Must wait scope to update
            $timeout(scope.onChange, 0);
          }
        };
      }
    };
  }

]);

/**
 * @ngdoc directive
 * @area api
 * @module form.directives
 * @name siret-field
 * @scope
 * @restrict EA
 * @description
 *
 *   A SIRET field for dynamic forms.
 *
 *   Uses validation-field directive for automatic layout, configuration and labels management.
 *
 * @param {string} name - The name of the field in the current form and in the model
 * @param {object} viewConfiguration - A view configuration as returned by the resource-manager service for example
 * @param {object} configuration - The configuration of the field extracted from a viewConfiguration object (in replacement to viewConfiguration)
 * @param {string} namespace - The configuration of the field extracted from a viewConfiguration object (in replacement to viewConfiguration)
 * @param {String} model - The model to bind to
 * @param {boolean} bare - Specify that the field should be left alone without label and help messages
 *
 * @example
 *
 *   `<siret-field name="siret" view-configuration="identificationConfiguration" data="association"></textare-field>`
 *
 */

angular.module('form.directives').directive('siretField', [
  '$timeout',
  'validationService',
  'formPreferences',
  '$translate',
  function ($timeout, validationService, formPreferences, $translate) {
    'use strict';

    return {
      restrict: 'EA',
      replace: true,
      templateUrl: 'form/form-directives/siret-field/siret-field.html',
      require: '^form',
      scope: {
        name: '@',
        viewConfiguration: '=',
        config: '=configuration',
        ns: '=namespace',
        model: '=',
        bare: '=',
        labelClass: '=?',
        asString: '=',
        _helpTooltip: '=helpTooltip',
        _labelWidth: '=labelWidth',
        _fieldWidth: '=fieldWidth',
        _helpWidth: '=helpWidth',
        _colType: '=colType', // Can choose between different col style (col-md or col-sm for example)
        valid: '=?',
        readOnly: '=',
        edit: '=',
        onChange: '&?',
        remoteValidation: '=?',
      },
      link: function (scope, element, attrs, form) {
        scope.configuration =
          scope.config ||
          _.get(scope, 'viewConfiguration.fields.' + scope.name) ||
          {};
        scope.namespace =
          scope.ns ||
          scope.configuration.ns ||
          _.get(scope, 'viewConfiguration.ns');
        scope.namespace = scope.namespace + '.' + scope.name;

        // Read remote validation from directive, configuration or viewConfiguration
        scope.remoteValidation =
          scope.remoteValidation !== undefined
            ? scope.remoteValidation
            : scope.configuration.remoteValidation !== undefined
              ? scope.configuration.remoteValidation
              : _.get(scope, 'viewConfiguration.remoteValidation') !== undefined
                ? _.get(scope, 'viewConfiguration.remoteValidation')
                : false;

        scope.helpTooltip =
          scope._helpTooltip === undefined
            ? formPreferences.config().helpTooltip
            : scope._helpTooltip;
        scope.labelWidth =
          scope._labelWidth || formPreferences.config().labelWidth || 4;
        scope.fieldWidth =
          scope._fieldWidth || formPreferences.config().fieldWidth || 4;
        scope.helpWidth =
          scope._helpWidth || formPreferences.config().helpWidth || 4;
        scope.colType =
          scope._colType || formPreferences.config().colType || 'md';
        var forbiddenSIRET =
          scope._forbiddenSIRET || scope.configuration.forbiddenSIRET;
        if (forbiddenSIRET) {
          scope.forbiddenSIRET = {
            SIREN: forbiddenSIRET.substring(0, 9),
            NIC: forbiddenSIRET.substring(9),
          };
        }
        scope.forbiddenSIRETLabel =
          scope._forbiddenSIRETLabel || scope.configuration.forbiddenSIRETLabel;

        scope.labels = {};
        _.each(['help', 'details'], function (index) {
          scope.labels[index] = $translate.instant(
            scope.namespace + '.' + index
          );
          scope.labels[index] =
            scope.labels[index] === scope.namespace + '.' + index
              ? ''
              : scope.labels[index];
        });

        var viewConfigurationLabel = scope.configuration.label;
        var tranlatedLabel = $translate.instant(scope.namespace + '.label');
        if (!_.isEmpty(viewConfigurationLabel)) {
          scope.label = viewConfigurationLabel;
        } else if (tranlatedLabel !== scope.namespace + '.label') {
          scope.label = tranlatedLabel;
        } else {
          scope.label = 'SIRET';
        }

        scope.form = form;
        scope.help = scope.configuration.help;

        scope.checkSiretValidity = function (siret, nic) {
          // SIRET is not invalid if empty.
          // It should block only if required is true
          if (!siret && !nic) {
            scope.valid = true;
          } else {
            scope.valid = validationService.verifySiret(siret, nic);
          }
          return scope.valid;
        };

        scope.isSIRETForbidden = function (siren, nic) {
          return (
            scope.forbiddenSIRET &&
            scope.forbiddenSIRET.SIREN === siren &&
            scope.forbiddenSIRET.NIC === nic
          );
        };

        // Detect invalid field
        scope.isFieldInvalid = function (siretField) {
          // Also check manually required
          return (
            scope.form[scope.name + '-' + siretField].$invalid ||
            (!scope.form[scope.name + '-' + siretField].$viewValue &&
              scope.configuration.required)
          );
        };
        // Detect required error
        scope.hasErrorRequired = function (siretField) {
          return (
            scope.form[scope.name + '-' + siretField].$error.required ||
            (!scope.form[scope.name + '-' + siretField].$viewValue &&
              scope.configuration.required)
          );
        };

        scope.SIRET = {};
        if (angular.isString(scope.model)) {
          var siret = scope.model.split(' ');
          scope.SIRET.SIREN = siret[0];
          scope.SIRET.NIC = siret[1];
        } else if (angular.isObject(scope.model)) {
          scope.SIRET.SIREN = scope.model.SIREN;
          scope.SIRET.NIC = scope.model.NIC;
        } else {
          scope.SIRET.SIREN = '';
          scope.SIRET.NIC = '';
        }

        // Management of the SIREN and NIC fields
        scope.status = {
          SIREN: null,
          NIC: null,
        };

        scope.pasteSIREN = function ($event) {
          if (scope.readOnly) {
            return;
          }
          scope.dirty = true;
          scope.valid = false;
          var pasted =
            $event.originalEvent.clipboardData.getData('text/plain') || '';
          var siret = pasted.replace(/(\W|_)+/g, '');
          scope.SIRET.SIREN = siret.substring(0, 9);
          if (siret.length > 9) {
            scope.SIRET.NIC = siret.substring(9);
          }
          form[scope.name + '-SIREN'].$setDirty(true);
          form[scope.name + '-NIC'].$setDirty(true);
        };

        scope.$watch(
          'SIRET',
          function () {
            if (scope.asString) {
              // We don't set the model if both parts are empty, else we have a whitespace or even "undefined " as value
              if (
                !_.isEmpty(scope.SIRET.SIREN) &&
                !_.isEmpty(scope.SIRET.NIC)
              ) {
                scope.model = _.join([scope.SIRET.SIREN, scope.SIRET.NIC], ' ');
              } else {
                scope.model = undefined;
              }
            } else {
              scope.model = scope.SIRET;
            }
            if (scope.onChange && scope.dirty) {
              $timeout(scope.onChange, 0);
              scope.dirty = false;
            }
          },
          true
        );
      },
    };
  },
]);

/**
 * @ngdoc directive
 * @area api
 * @module form.directives
 * @name steps-wizard
 * @scope
 * @transclude
 * @restrict EA
 * @description
 *
 *   Display a steps wizard helper as seen on top of pages from a data collect workflow.
 *
 * @param {array<string>} steps - The list of step ids to display.
 * @param {string} labelsPrefix - The prefix to prepend to a step's id to get the key of the label to be fetched using angular-translate.
 * @param {string} active - The currently active step.
 *
 * @example
 *
 *   `<steps-wizard steps="['demandeur', 'beneficiaire', 'aide']" labels-prefix="'myConfig.steps'" active="demandeur"></steps--wizard>`
 *
 */

angular.module('form.directives').directive('stepsWizard', [
  '$translate',
  function ($translate) {
    'use strict';

    return {
      restrict: 'EA',
      replace: true,
      transclude: true,
      templateUrl: 'form/form-directives/stepswizard/stepswizard.html',
      scope: {
        steps: '=',
        labelsPrefix: '=',
        active: '=',
        stepLabel: '@',
        activeStepLabel: '@',
        customStepsNames: '=',
      },
      link: function (scope) {
        // use custom steps names if provided and if array
        scope.useCustomNames =
          Array.isArray(scope.customStepsNames) &&
          !!scope.customStepsNames.length;

        /**
         * Check if the step is active or not
         * @param {number} index
         * @param {string} step
         * @return {boolean}
         */
        scope.isStepActive = function (index, step) {
          return (
            index === scope.active ||
            scope.active === step ||
            step.indexOf(scope.active) !== -1
          );
        };

        /**
         * Get step title
         *
         * @param {number} index
         * @param {string} step
         * @returns {string}
         */
        scope.getStepTitle = function (index, step) {
          var titleParts = [
            scope.stepLabel,
            index + 1,
            '-',
            $translate.instant(scope.labelsPrefix + '.' + step),
          ];

          if (scope.isStepActive(index, step)) {
            titleParts.push(scope.activeStepLabel);
          }
          
          return titleParts.join(' ');
        };
      },
    };
  },
]);

/**
 * @ngdoc directive
 * @area api
 * @module form.directives
 * @name tags-field
 * @scope
 * @restrict EA
 * @description
 *
 *   A tags field: select several value from a source in a text-field using tags.
 *   By default, items are registered in Link objects ({href: '{{item.id}}'})
 *
 *   Uses validation-field directive for automatic layout, configuration and labels management.
 *
 * @param {string} name - The name of the field in the current form and in the model
 * @param {object} viewConfiguration - A view configuration as returned by the resource-manager service for example
 * @param {object} configuration - The configuration of the field extracted from a viewConfiguration object (in replacement to viewConfiguration)
 * @param {string} namespace - The configuration of the field extracted from a viewConfiguration object (in replacement to viewConfiguration)
 * @param {String} model - The model to bind to
 * @param {object} source - List of available data
 * @param {string} displayProperty - What to display and search from the browsed values (can't be deep)
 * @param {string} keyProperty - Key identifying each items from source (can't be deep) (default: 'id')
 * @param {bool} autocomplete - Enable autocomplete from a source (default: true)
 * @param {string} binding - How the field should register data: 'link' (default), 'clone' or 'value'
 * @param {bool} notRestricted - Allow the user to enter any value (not compatible with 'link' binding) (default: false)
 *
 * @example
 *
 *   `<tags-field
 *     name="intervenants"
 *     view-configuration="viewConfiguration"
 *     model="visite.intervenants"
 *     source="mdm.employes"
 *     display-key="completeName">
 *   </tags-field>`
 *
 */

angular.module('form.directives').directive('tagsField', ['$q', '$timeout',
  function($q, $timeout) {
    'use strict';

    return {
      restrict: 'EA',
      replace: true,
      templateUrl: 'form/form-directives/tags-field/tags-field.html',
      require: '^form',
      scope: {
        name: '@',
        viewConfiguration: '=',
        config: '=configuration',
        ns: '=namespace',
        model: '=',
        source: '=',
        displayProperty: '@',
        keyProperty: '@?',
        autocomplete: '=?',
        placeholder: '@?',
        binding: '@?',
        nbResults: '@?',
        notRestricted: '=?',
        readOnly: '=?',
        displayInvalidTagMessage: '=',
        onChange: '&?',
        remoteValidation: '=?',
        labelClass: '=?'
      },
      link: function(scope, element, attrs, ctrl) {
        // Field configuration
        scope.configuration = scope.config || _.get(scope, 'viewConfiguration.fields.' + scope.name) || {};
        scope.namespace = scope.ns || scope.configuration.ns || _.get(scope, 'viewConfiguration.ns');
        scope.namespace = scope.namespace;
        scope.placeholder = scope.placeholder || scope.namespace + '.' + scope.name + '.placeholder';
        scope.readOnly = scope.readOnly || false;

        // Read remote validation from directive, configuration or viewConfiguration
        scope.remoteValidation = scope.remoteValidation !== undefined ? scope.remoteValidation :
          scope.configuration.remoteValidation !== undefined ? scope.configuration.remoteValidation :
            _.get(scope, 'viewConfiguration.remoteValidation') !== undefined ? _.get(scope, 'viewConfiguration.remoteValidation') : false;

        // Mode and default values
        scope.autocomplete = scope.autocomplete === undefined ? true : scope.autocomplete;
        scope.binding = scope.binding || 'link';
        scope.nbResults = scope.nbResults || 500;
        scope.notRestricted = (scope.notRestricted && scope.binding !== 'link') || false;
        scope.keyProperty = scope.binding === 'value' ? scope.displayProperty : scope.keyProperty || 'id';
        // scope.status.filled is used to aknowledge if the model is empty or not
        scope.status = {filled: ''};

        var prepareTagsData = function() {
          // Copying the model items into tagsData
          if (scope.binding === 'clone') {
            scope.tagsData = angular.copy(scope.model);
          }
          else {
            scope.tagsData = _.map(scope.model, function(item) {
              // Transforming labels into objects
              var tagItem = {};
              if (scope.binding === 'value') {
                tagItem[scope.displayProperty] = item;
              }
              // Using identifiers (by default, 'id')
              else if (scope.binding === 'link' || true) {
                tagItem[scope.keyProperty] = item.href;
                var itemSource = _.find(scope.source, function(masterItem) {
                  return masterItem[scope.keyProperty] === item.href;
                });
                tagItem[scope.displayProperty] = itemSource ? itemSource[scope.displayProperty] : item.title;

              }
              return tagItem;
            });
          }
        };

        scope.model = scope.model || [];
        if (!scope.autocomplete) {
          // without autocompletion, the model is just an array of strings, and there is no need for loading data
          scope.binding = 'value';
          scope.displayProperty = 'text';
          scope.notRestricted = true;
          scope.tagsData = _.map(scope.model, function(tag) {
            return {text: tag};
          });
          scope.loadingData = false;
        }
        else {
          scope.tagsData = [];
          scope.loadingData = true;
        }
        scope.$watch('source', function() {
          if (scope.source && scope.autocomplete) {
            prepareTagsData();
            scope.loadingData = false;
          }
        });

        var fieldUpdate = false;
        // Update tagsData when external change occures to the model
        scope.$watch('model', function() {
          // WARNING : changes coming from the field must not be taken into account!
          if (!fieldUpdate) {
            // Update tagsData
            if (scope.autocomplete && scope.source) {
              fieldUpdate = true;
              prepareTagsData();
            }
            else if (!scope.autocomplete) {
              fieldUpdate = true;
              _.each(scope.model, function(tag) {
                scope.tagsData.push({text: tag});
              });
            }
          }
          else {
            // Get ready for potential next edit
            fieldUpdate = false;
          }
        }, true);

        scope.$watch('tagsData', function(newVal) {
          // Each change, we copy what's into tagsData into model, transforming it accordingly
          if (newVal && !scope.loadingData) {
            scope.status.filled = _.isEmpty(newVal) ? '' : 'true';
            // WARNING : changes coming from outside must not be taken into account!
            if (!fieldUpdate) {
              fieldUpdate = true;
              scope.model = scope.tagsData.map(function(item) {
                if (scope.binding === 'value') {
                  // No autocomplete: only the tag is copied
                  return item[scope.displayProperty];
                }
                else if (scope.binding === 'clone') {
                  // Cloning the entire item into the model
                  return angular.copy(item);
                }
                else if (scope.binding === 'link' || true) {
                  // Default behavior: a Link is created using the identifier ('id')
                  return {
                    href: item[scope.keyProperty],
                    title: item[scope.displayProperty]
                  };
                }
              });
              if (scope.onChange) {
                $timeout(scope.onChange, 0);
              }
            }
            else {
              // Get ready for potential next edit
              fieldUpdate = false;
            }
          }
        }, true);

        // Watch form and field $valid fields
        if (scope.displayInvalidTagMessage) {
          scope.form = ctrl;
          scope.$watch('form.$valid', function(newValue) {
            if (newValue !== undefined) {
              scope.invalidForm = !newValue;

              // Force field $valid to true when form is $valid
              if (!scope.invalidForm) {
                scope.form[scope.name].$valid = newValue;
                scope.form[scope.name].$invalid = !newValue;
                scope.invalidField = !newValue;
              }
            }
          });
          scope.$watch('form[scope.name]', function(newValue) {
            if (newValue !== undefined) {
              scope.invalidField = !newValue;
            }
          });
        }

        // A tag is valid only if it has an identifier in the source of data
        // The function also update the invalid variable used to track errors on the field
        scope.validTag = function(value) {
          var returnValue = scope.notRestricted || !!(value[scope.keyProperty] && _.find(scope.source, function(item) {
            return item[scope.keyProperty] === value[scope.keyProperty];
          }));
          if (scope.displayInvalidTagMessage) {
            // $valid and $invalid are not updated automatically, so set them from here
            scope.form[scope.name].$valid = returnValue;
            scope.form[scope.name].$invalid = !returnValue;
            scope.invalidField = !returnValue;
          }
          return returnValue;
        };

        // searching from the source
        scope.filterSource = function(source, query, property) {
          return $q(function(resolve) {
            resolve(source.filter(function(item) {
              return !query || _.get(item, property).toUpperCase().indexOf(query.toUpperCase()) !== -1;
            }));
          });
        };
      }
    };
  }

]);

/**
 * @ngdoc directive
 * @area api
 * @module form.directives
 * @name text-field
 * @scope
 * @restrict EA
 * @description
 *
 *   A basic text field for dynamic forms.
 *
 *   Uses validation-field directive for automatic layout, configuration and labels management.
 *
 * @param {string} name - The name of the field in the current form and in the model
 * @param {object} viewConfiguration - A view configuration as returned by the resource-manager service for example
 * @param {object} configuration - The configuration of the field extracted from a viewConfiguration object (in replacement to viewConfiguration)
 * @param {string} namespace - The configuration of the field extracted from a viewConfiguration object (in replacement to viewConfiguration)
 * @param {String} model - The model to bind to
 * @param {boolean} bare - Specify that the field should be left alone without label and help messages
 *
 * @example
 *
 *   `<text-field name="dateNaissance" view-configuration="identificationDemandeurConfiguration" data="demandeur"></text-field>`
 *
 */
angular.module('form.directives').directive('textField', ['$timeout', 'formUtils',
  function ($timeout, formUtils) {
    'use strict';

    return {
      restrict: 'EA',
      replace: true,
      templateUrl: 'form/form-directives/text-field/text-field.html',
      scope: {
        name: '@',
        viewConfiguration: '=',
        config: '=configuration',
        ns: '=namespace',
        model: '=',
        format: '@',
        bare: '=',
        displayCharactersLeft: '=?',
        remoteValidation: '=?',
        readOnly: '=',
        onChange: '&?',
        labelClass: '=?',
        autocomplete: '=?'
      },
      link: function (scope) {
        function init() {
          scope.configuration = scope.config || _.get(scope, 'viewConfiguration.fields.' + scope.name) || {};
          scope.namespace = scope.ns || scope.configuration.ns || _.get(scope, 'viewConfiguration.ns');
          scope.format = scope.format || scope.configuration.format;

          scope.configuration.minlength = _.get(scope.configuration, 'restrictions[0].minLength', scope.configuration.minlength || 0);
          scope.configuration.maxlength = _.get(scope.configuration, 'restrictions[0].maxLength', scope.configuration.maxlength || 100);

          scope.charLeft = {
            display: false,
            value: scope.configuration.maxlength
          };
          updateCharleftValue();

          readGlobalOption('displayCharactersLeft');
          readGlobalOption('remoteValidation');

          // Put model into a 'data' object
          formUtils.wrapPrimitiveInObject(scope, 'model', 'data');
        }

        init()

        // Read option from directive, configuration or viewConfiguration
        function readGlobalOption(optionName) {
          scope[optionName] = scope[optionName] !== undefined ? scope[optionName] :
            scope.configuration[optionName] !== undefined ? scope.configuration[optionName] :
              _.get(scope, 'viewConfiguration.' + optionName) !== undefined ? _.get(scope, 'viewConfiguration.' + optionName) : false;
        }
        /**
         * Update the number of characters left on the input
         */
        function updateCharleftValue() {
          scope.charLeft.value = scope.configuration.maxlength - _.get(scope.model, 'length', 0);
        }

        scope.isRequired = function () {
          return !scope.remoteValidation && !scope.configuration.hidden && scope.configuration.required;
        }

        scope.$watch('model', function (newValue) {
          // model watch is called when wrapPrimitiveInObject ran so it can be trigger with a readoinly config
          if (scope.readOnly) {
            return;
          }

          if (newValue) {
            //Remove know characters \u2018\u2019 by '
            //Remove special characters (only accepts ISO 8859-15 characters)
            scope.model = newValue
              .replace(/[\u2018\u2019] /g, '\'')
              .replace(/[^\x20-\x7E\xA0-\xA3\xA5\xA7\xA9-\xB3\xB5-\xB7\xB9-\xBB\xBF-\xFF\u20AC\u0160\u0161\u017D\u017E\u0152\u0153\u0178]/g, '');
            scope.data.model = scope.model
          }
          if (scope.displayCharactersLeft) {
            $timeout(function () {
              updateCharleftValue()
            });
          }
        });

        // Callback on changes
        scope.change = function () {
          if (scope.onChange) {
            // Must wait scope to update
            $timeout(scope.onChange, 0);
          }
        };

        // Added a timeout to undisplay character left message.
        // This timeout must give to the user time to click on a form button at the bottom of the page.
        // Without this timeout, when the message disappears, the button pull up in the form and the click on the button is not always effective.
        scope.waitUndisplayCharacterLeft = function () {
          $timeout(function () {
            scope.charLeft.display = false;
          }, 100);
        };
      }
    };
  }
]);

/**
 * @ngdoc directive
 * @area api
 * @module form.directives
 * @name text-field
 * @scope
 * @restrict EA
 * @description
 *
 * Follow this recommendation from iframe-resizer: https://github.com/davidjbradshaw/iframe-resizer#iframe-not-detecting-textarea-resizes
 * But in the angular way, ie with a directive
 *
 * This will detect resizing of textareas or content editable fields and trigger resizing of the parent iframe if it is available and iframe-resizer was correctly loaded.
 *
 * @example
 *
 *   `<textarea text-resize-iframe></textarea>`
 *
 */
angular.module('form.directives').directive('textResizeIframe', [
  function() {
    'use strict';

    return {
      restrict: 'A',
      link: function(scope, element) {
        var x;
        var y;

        function store() {
          x = element[0].offsetWidth;
          y = element[0].offsetHeight;
        }

        element.on('mousemove', function() {
          if (element[0].offsetWidth !== x || element[0].offsetHeight !== y) {
            store();
            if ('parentIFrame' in window) {
              window.parentIFrame.size();
            }
          }
        });
      }
    };
  }
]);

/**
 * @ngdoc directive
 * @area api
 * @module form.directives
 * @name textarea-field
 * @scope
 * @restrict EA
 * @description
 *
 *   A basic textarea field for dynamic forms.
 *
 *   Uses validation-field directive for automatic layout, configuration and labels management.
 *
 * @param {string} name - The name of the field in the current form and in the model
 * @param {object} viewConfiguration - A view configuration as returned by the resource-manager service for example
 * @param {object} configuration - The configuration of the field extracted from a viewConfiguration object (in replacement to
 * viewConfiguration)
 * @param {string} namespace - The configuration of the field extracted from a viewConfiguration object (in replacement to viewConfiguration)
 * @param {String} model - The model to bind to
 * @param {boolean} bare - Specify that the field should be left alone without label and help messages
 *
 * @example
 *
 *   `<textarea-field name="dateNaissance" view-configuration="identificationDemandeurConfiguration" data="demandeur"></textare-field>`
 *
 */
angular.module('form.directives').directive('textareaField', ['$log',  '$timeout', 'formUtils',
  function($log, $timeout, formUtils) {
    'use strict';

    return {
      restrict : 'EA',
      replace : true,
      templateUrl : 'form/form-directives/textarea-field/textarea-field.html',
      require: '^form',
      scope : {
        name : '@',
        viewConfiguration : '=',
        config : '=configuration',
        ns : '=namespace',
        model : '=',
        bare : '=',
        displayCharactersLeft: '=?',
        remoteValidation: '=?',
        format : '@',
        readOnly : '=',
        rows : '=',
        labelClass: '=?'
      },
      link : function(scope, element, attrs, formCtrl) {

        scope.configuration = scope.config || _.get(scope, 'viewConfiguration.fields.' + scope.name) || {};
        scope.namespace = scope.ns || scope.configuration.ns || _.get(scope, 'viewConfiguration.ns');

        scope.format = scope.format || scope.configuration.format;

        scope.charLeft = {
          display: false
        };

        // Read option from directive, configuration or viewConfiguration
        function readGlobalOption(optionName) {
          scope[optionName] = scope[optionName] !== undefined ? scope[optionName] :
            scope.configuration[optionName] !== undefined ? scope.configuration[optionName] :
              _.get(scope, 'viewConfiguration.' + optionName) !== undefined ? _.get(scope, 'viewConfiguration.' + optionName) : false;
        }
        readGlobalOption('displayCharactersLeft');
        readGlobalOption('remoteValidation');

        scope.maxLength = _.get(scope.configuration, 'restrictions[0].maxLength', scope.configuration.maxlength || 4000);
        scope.charactersLeftData = {};
        scope.charactersLeftData.value = scope.maxLength;

        formUtils.wrapPrimitiveInObject(scope, 'model', 'data');

        scope.tools = [
          'bold',
          'italic',
          'underline',
          'strikethrough',
          'justifyLeft',
          'justifyCenter',
          'justifyRight',
          'justifyFull',
          'insertUnorderedList',
          'insertOrderedList',
          'indent',
          'outdent',
          'createLink',
          'unlink',
          'formatting',
          'fontName',
          'fontSize',
          'foreColor',
          'backColor',
          'cleanFormatting'
        ];

        // In this mode, the text is automatically process to remove the unnecessary style
        // And the toobars have less options
        if(scope.configuration.simpleMode === true) {
          scope.tools = [
            'bold',
            'italic',
            'underline',
            'strikethrough',
            'justifyLeft',
            'justifyCenter',
            'justifyRight',
            'justifyFull',
            'insertUnorderedList',
            'insertOrderedList',
            'indent',
            'outdent',
            'createLink',
            'unlink',
            'formatting'
          ];

          // Style management when the data changed
          scope.$watch('model', function(value) {
            if(scope.configuration.richtext !== true) { return; }

            // In this case, the input is a simple text, we must not try to handle it as HTML
            var htmlRegex = /<([A-Za-z][A-Za-z0-9]*)\b[^>]*>(.*?)<\/\1>/;
            if(!htmlRegex.test(value)) {
              return;
            }

            // If the input is a valid HTML, we will handle it and remove unnecessary style
            var $result;
            try {
              $result = $('<div></div>').append($(value));
            } catch(e) {
              $log.warn('textarea-field - failed to parse some content detected as HTML', e.message);
            }
            var editable = element.find('[contenteditable]');
            $(editable).html($result.html());
            // replace all styles except the ones that can be edited in the editor
            $.each($(editable).find('*'), function(idx, val) {
              var $item = $(val);
              if ($item.length > 0) {
                var saveStyle = {
                  'font-weight' : $item.css('font-weight'),
                  'font-style' : $item.css('font-style'),
                  'text-align' : $item.css('text-align'),
                  'text-decoration' : $item.css('text-decoration')
                };
                $item.removeAttr('style').removeClass().css(saveStyle);
              }
            });
            // remove unnecesary tags (if paste from word)
            $(editable).children('style').remove();
            $(editable).children('meta').remove();
            $(editable).children('link').remove();
          });
        }

        // Handle form $dirty and $pristine
        scope.kendoEditorChange = function kendoEditorChange() {
      	formCtrl[attrs.name].$dirty = true;
          formCtrl[attrs.name].$pristine = false;
          formCtrl.$dirty = true;
          formCtrl.$pristine = false;
        };

        //Remove know characters \u2018\u2019 by '
        //Remove special characters (only accepts ISO 8859-15 characters)
        scope.$watch('model', function(newValue) {
          if (newValue && !scope.readOnly) {
            var valueISO885915 = newValue.replace(/[\u2018\u2019] /g, '\'');
            valueISO885915 = valueISO885915.replace(/[^\n\x20-\x7E\xA0-\xA3\xA5\xA7\xA9-\xB3\xB5-\xB7\xB9-\xBB\xBF-\xFF\u20AC\u0160\u0161\u017D\u017E\u0152\u0153\u0178]/g, '');
            scope.model = valueISO885915;
          }
          if(scope.displayCharactersLeft) {
            scope.charactersLeftData.value = scope.maxLength - _.get(scope, 'model.length', 0);
          }
        });

        scope.$on('$destroy', function() {
        });

        // Added a timeout to undisplay character left message.
        // This timeout must give to the user time to click on a form button at the bottom of the page.
        // Without this timeout, when the message disappears, the button pull up in the form and the click on the button is not always effective.
        scope.waitUndisplayCharacterLeft = function(){
          $timeout(function () {
            scope.charLeft.display = false;
          }, 100);
        };
      }
    };
  }]);

/* global _ */

/**
 * @ngdoc directive
 * @area api
 * @module form.directives
 * @name validation-field
 * @scope
 * @transclude
 * @restrict EA
 * @description
 *
 *   Add standard patterns on a form field.
 *
 *   Standard layout of label, help message, etc. Automatic labels management, automatic common field configuration management.
 *
 *   This directive will expect to find in its children elements a form field (input, textarea, select) with a name and a ngModel.
 *   This name will be used to fetch the labels using angular.translate with the given namespace.
 *   It will also fetch the field configuration in the viewConfiguration.fields object (required, pattern, maxlength, etc.).
 *
 *
 *
 * @param {object} viewConfiguration - The configuration of the current page or section of page. As returned by the resource-manager service. If not given it will be fetched from the parent scope.
 * @param {object} configuration - The configuration of the field. If not given, validartion-field will look into viewConfiguration.fields[name]
 * @param {string} namespace - Prefix use to load labels with $translate
 *
 * All parameters below can also be written in the field's configuration
 *
 * @param {boolean} displayIcons - Set to true to display font-awesome icons along to the messages surrounding the field (validity, required, etc).
 * @param {boolean} displayWarning - Set to true to display a warning when the field is invalid even if the form was not attempted.
 * @param {string} labelClass - Add a css class to the label message.
 * @param {string} fieldClass - Add a css class to the field message.
 * @param {number} labelWidth - Set a class col-md-'labelWidth' to the label message. Defaults to 4.
 * @param {number} fieldWidth - Set a class col-md-'fieldWidth' to the field. Defaults to 4.
 * @param {number} helpWidth - Set a class col-md-'helpWidth' to the help message. Defaults to 4.
 * @param {string} colType - Indicates if previous variables are to be set to a class col-md- or col-sm.
 * @param {boolean} noExtendField - Specify that the field should take the room of the help message even if it is empty. By default it will.
 * @param {boolean} bare - Specify that the field should be left alone without label and help messages.
 * @param {boolean} errorsInline - Specify that errors should be displayed next to the field, instead of below.
 * @param {boolean} helpTooltip - Specify that the help message should be displayed using a helpTooltip
 *
 * @example
 *
 *   ```
 *   <validation-field configuration="configuration" namespace="viewConfiguration.ns">
 *     <input name="name" ng-model="user.name" placeholder="{{viewConfiguration.ns + '.' + name + '.placeholder' | translate}}" ng-required="configuration.required">
 *   </validation-field>
 *   ```
 *
 */

angular.module('form.directives').directive('validationField', [
  '$log',
  '$translate',
  '$timeout',
  '$rootScope',
  'formPreferences',
  '$sce',
  function ($log, $translate, $timeout, $rootScope, formPreferences, $sce) {
    'use strict';

    /**
     * Find the form element of transclude content
     * @param  {HtmlElement} element [description]
     * @return {HtmlElement}         Reference of the form element of transclude content
     */
    var getFormElement = function (element) {
      var formElement = element.find('input');
      formElement = formElement.length === 0 ? element.find('select') : formElement;
      formElement = formElement.length === 0 ? element.find('textarea') : formElement;
      // Some kendo directives have more than one element
      if (formElement.length > 1) {
        formElement = element.find('input[name]');
      }
      return formElement;
    };

    /**
     * Find all form elements of transclude content
     * @param  {HtmlElement} element [description]
     * @return {HtmlElement}         Reference of all form elements of transclude content
     */
    var getAllFormElements = function (element) {
      var formElement = element.find('input');
      formElement = formElement.length === 0 ? element.find('select') : formElement;
      formElement = formElement.length === 0 ? element.find('textarea') : formElement;
      return formElement;
    };

    return {
      restrict: 'EA',
      replace: false,
      transclude: true,
      templateUrl: 'form/form-directives/validation-field/validation-field.html',
      require: ['^form'],
      scope: {
        viewConfiguration: '=',
        configuration: '=',
        namespace: '=',
        displayIcons: '=',
        displayWarning: '=',
        labelClass: '=?',
        labelFor: '=?',
        fieldClass: '=',
        labelWidth: '=', // will be added to a col-md- class
        fieldWidth: '=',
        helpWidth: '=',
        colType: '=', // Can choose between different col style (col-md or col-sm for example)
        noExtendField: '=',
        bare: '=',
        errorsInline: '=',
        helpTooltip: '=',
        groupInputs: '=?',
        baseInputInformations: '=?', // use to link the input field to the infos via aria-describedby
      },
      link: function (scope, element, attrs, ctrls) {
        var formController = ctrls[0];
        var watches = [];
        // Form instance
        scope.form = formController;

        // Field
        var formElement = getFormElement(element);
        scope.fieldName = formElement.attr('name') || scope.configuration.reference || scope.$parent.name;

        // Retrieve field status in form data
        scope.field = scope.form[scope.fieldName];
        if (!scope.field) {
          $log.error('validationField : A field with name and ng-model attributes must be defined.');
        }

        if (!formElement.attr('id') && formElement.attr('name')) {
          formElement.attr('id', formElement.attr('name'));
        }

        var inputInfo = '';

        /**
         * Process field's configuration
         * Read field properties (required, hidden) and initialize labels
         */
        var processConfiguration = function () {
          var fieldConfiguration = {};
          // Search viewConfiguration in parent if not given
          var viewConfiguration = scope.viewConfiguration || scope.$parent.viewConfiguration || {};

          // Copy scope configuration
          if (scope.configuration) {
            fieldConfiguration = angular.copy(scope.configuration);
            // Or find it in viewConfiguration
          } else if (_.get(viewConfiguration, 'fields.' + scope.fieldName)) {
            fieldConfiguration = angular.copy(viewConfiguration.fields[scope.fieldName]);
          } else {
            $log.warn(
              'No configuration found for field ' +
                scope.fieldName +
                '. Did you forget to pass a configuration or viewConfiguration to validation-field?'
            );
          }
          // the namespace is used to find translated labels for this field.
          // it can be configured, or written in viewConfiguration
          fieldConfiguration.ns = scope.namespace || fieldConfiguration.ns || viewConfiguration.ns;

          // duplicate what check-validation-field does (set validity on specific rules), but descending from a configuration object.
          if (fieldConfiguration.validation) {
            watches.push(
              scope.$parent.$watch(fieldConfiguration.validation, function (isValid) {
                scope.field.$setValidity('checkValidity', isValid);
              })
            );
          }

          // Rendering parameters can be given as scope paremeters, view configuration, or simply as keys to the configuration
          var formPreferencesConfig = formPreferences.config();
          [
            'displayIcons',
            'displayWarning',
            'labelClass',
            'labelFor',
            'fieldClass',
            'labelWidth',
            'fieldWidth',
            'helpWidth',
            'colType',
            'noExtendField',
            'bare',
            'errorsInline',
            'helpTooltip',
            'loading',
            'tooltipPlacement',
            'groupInputs',
            'baseInputInformations',
          ].forEach(function (param) {
            if (!fieldConfiguration[param]) {
              fieldConfiguration[param] = scope[param] || viewConfiguration[param] || formPreferencesConfig[param];
            }
          });

          // The label 'for' attributes should be the form id
          fieldConfiguration.labelFor = fieldConfiguration.labelFor || formElement.attr('id');

          // When the input is of type 'radio' a fieldset should be used by default
          fieldConfiguration.groupInputs = fieldConfiguration.groupInputs || formElement.attr('type') === 'radio';

          // Evaluation of required attribute (in configuration or on the element)
          scope.required = fieldConfiguration.required || formElement.attr('required');
          // Dynamically watch the changes on eventual ng-required
          if (formElement.attr('ng-required') && formElement.attr('ng-required').length > 0) {
            watches.push(
              scope.$parent.$watch(formElement.attr('ng-required'), function (required) {
                scope.required = fieldConfiguration.required || required;
              })
            );
          }

          // Status management
          scope.status = null;
          // Detect a required error without using DOM
          scope.hasRequiredError = function () {
            var viewValue = _.get(scope, 'field.$viewValue');
            return fieldConfiguration.required && [undefined, null, ''].indexOf(viewValue) > -1;
          };
          // Indicates if errors can be shown
          scope.displayErrors = function () {
            var displayError =
              (scope.status === 'error' && scope.field.$dirty) || scope.form.$submitted || scope.form.attempted;
            return displayError;
          };
          // Changes status on blur
          scope.blur = function () {
            if (scope.field.$invalid || scope.hasRequiredError()) {
              scope.status = 'error';
            } else {
              scope.status = 'success';
            }
            scope.$digest();
          };
          formElement.bind('blur', scope.blur);

          // Show errors on any circumstance if global option is set
          scope.showErrors = viewConfiguration.showErrors;

          /**
           * Returns the error key according to the field configuration and the nature of the error
           * @returns {string}
           */
          scope.getErrorKey = function () {
            var field = scope.field;
            var displayErrors = scope.displayErrors();
            var isInvalid = field.$invalid;

            if (
              (isInvalid && field.$error.required && (displayErrors || scope.showErrors)) ||
              (scope.hasRequiredError() && scope.showErrors)
            ) {
              return 'required';
            }

            if (isInvalid && displayErrors && field.$dirty) {
              if (field.$error.minlength) {
                return 'minlength';
              }
              if (field.$error.maxlength) {
                return 'maxlength';
              }
              if (field.$error.max) {
                return 'max';
              }
              if (field.$error.min) {
                return 'min';
              }
              if (field.$error.confirmationField) {
                return 'confirmationField';
              }
              if (field.$error.checkValidity) {
                this.setPatternOnCheckValidityIfNeeded();
                return 'checkValidity';
              }
              if (field.$error.parse || field.$error.editable || field.$error.pattern) {
                return 'pattern';
              }

              return 'validationError';
            }
            return '';
          };

          /**
           * Set pattern error instead of checkValidity if needed
           */
          scope.setPatternOnCheckValidityIfNeeded = function() {
            var defaultCheckValidityMessage = $translate.instant('form.field.error.checkValidity');
            var defaultPatternMessage = $translate.instant('form.field.error.pattern');
            var fieldErrors = scope.fieldConfiguration.error;

            if(fieldErrors['checkValidity'] === defaultCheckValidityMessage && fieldErrors['pattern'] !== defaultPatternMessage) {
              fieldErrors['checkValidity'] = fieldErrors['pattern'];
            }
          };

          /**
           * Initializes labels and the field's display
           */
          var writeField = function () {
            // Complete field configuration with standard labels
            _.each(
              [
                'label',
                'unit',
                'help',
                'placeholder',
                'details',
                'error.required',
                'error.pattern',
                'error.min',
                'error.max',
                'error.minlength',
                'error.maxlength',
                'error.confirmationField',
                'error.checkValidity',
                'error.validationError',
              ],
              function (key) {
                var lbKey = fieldConfiguration.ns + '.' + scope.fieldName + '.' + key;

                // Initialize the value with the configuraiton field;
                var lb = _.get(fieldConfiguration, key, null);
                if (!lb && $translate.instant(lbKey) !== lbKey) {
                  lb = $translate.instant(lbKey);
                }

                if (lb === lbKey || lb === null || lb === undefined) {
                  var keyDefault = 'form.field.' + key;
                  var lbDefault = $translate.instant(keyDefault);
                  if (lbDefault !== keyDefault) {
                    lb = lbDefault;
                  }
                }

                _.set(fieldConfiguration, key, lb);
              }
            );

            // Set details as trusted Html
            fieldConfiguration.details = $sce.trustAsHtml(fieldConfiguration.details);

            // Set width attributes
            scope.widths = {
              label: fieldConfiguration.labelWidth || 4,
              field: fieldConfiguration.fieldWidth || 4,
              help: fieldConfiguration.helpWidth || 4,
              colType: fieldConfiguration.colType || 'md',
            };

            if (
              !(fieldConfiguration.help && !fieldConfiguration.helpTooltip) &&
              !fieldConfiguration.noExtendField &&
              !fieldConfiguration.errorsInline
            ) {
              scope.widths.field += scope.widths.help;
            }

            if (fieldConfiguration.bare) {
              scope.widths.field = fieldConfiguration.fieldWidth || 12;
            }

            // Get base input information attributes for aria-describedby
            inputInfo = (fieldConfiguration.baseInputInformations || '') + ' ';
            if (fieldConfiguration.help) {
              inputInfo += scope.fieldName + '-help ';
            }
            if (fieldConfiguration.details) {
              inputInfo += scope.fieldName + '-details ';
            }
            if (!fieldConfiguration.errorsInline || (fieldConfiguration.errorsInline && !fieldConfiguration.bare)) {
              inputInfo += scope.fieldName + '-errors ';
            }
            getAllFormElements(element).attr('aria-describedby', inputInfo);
          };

          // Has to be called whenever translations are loaded
          watches.push($rootScope.$on('$translateChangeSuccess', writeField));
          writeField();

          // Finally write configuration into scope
          scope.fieldConfiguration = fieldConfiguration;
        };

        // Process configuration on every changes
        watches.push(
          scope.$watch(
            '[namespace, configuration, viewConfiguration]',
            function () {
              processConfiguration();
            },
            true
          )
        );

        // Remove watches and bound functions
        scope.$on('$destroy', function () {
          formElement.unbind('blur', scope.blur);
          _.each(watches, function (watch) {
            watch();
          });
        });
      },
    };
  },
]);

/**
 * @ngdoc directive
 * @area api
 * @module form.directives
 * @name validation-form
 * @restrict A
 * @description
 *
 *   A form with some added behaviour.
 *
 *   Will add a 'attempted' boolean on the form's controller.
 *
 *   Will set focus to the first field in error when form submission fails because of some invalidity.
 *
 * @param {function} validationForm - The function to run when the form is submitted and valid.
 *
 * @example
 *
 *   `<form name="userCreateForm" novalidate validation-form="createUser()">...</form>`
 *
 */

angular.module('form.directives').directive('validationForm', ['$parse', '$log', '$window',
  function($parse, $log, $window) {
    'use strict';

    return {
      restrict: 'A',
      replace: true,
      transclude: false,
      link: function(scope, formElement, attributes) {

        var watches = [];
        // Initialisation de la variable à l'instanciation de la directive
        var formName = attributes.name;
        if (formName) {
          scope[formName].attempted = false;
        } else {
          $log.error('validationForm : The attribute \'name\' must be define.');
        }

        // A parent controller can "spy" on the scope state by giving an object to fill
        if (attributes.formWatch) {
          var watcherName = attributes.formWatch;
          watches.push(scope.$watch(formName + '.$valid', function() {
            scope[watcherName].attempted = scope[formName].attempted;
            scope[watcherName].$valid = scope[formName].$valid;
            scope[watcherName].$invalid = scope[formName].$invalid;
          }, true));
        }

        // Fonction à exécuter lorsque le formulaire est valide
        var fn = $parse(attributes.validationForm);
        // Fonction à exécuter lorsque le formulaire est invalide
        var fnI = $parse(attributes.invalid);

        formElement.submit(function(event) {

          function formSubmit() {

            if (scope[formName].$valid) {
              scope[formName].attempted = false;
              // Reset dirty, pristine and untouched state on form
              scope[formName].$setPristine();
              scope[formName].$setUntouched();
              if (fn) {
                fn(scope, {
                  $event: event
                });
              }
            } else {
              scope[formName].attempted = true;

              // Sélection du premier champ en erreur (ignore les inputs façades de kendo)
              var firstElementOnError = $('#' + formName + ' .ng-invalid:not(.k-formatted-value):first');

              if (firstElementOnError) {

                // Recupération des coordonnées du premier champ en erreur afin
                // de repositionner l'écran de l'utilisateur dessus.
                // Gestion d'une intégration page simple ou bien iframe
                var positionElement = firstElementOnError.offset();

                if (positionElement) {
                  var giveFocus = function() {
                    // Particular case for kendo widgets that use
                    // 2 inputs element : one hidden and one displayed
                    if (firstElementOnError.hasClass('k-widget')) {
                      firstElementOnError = firstElementOnError.find('input')[0];
                    }
                    // Donner le focus au premier champ invalide
                    firstElementOnError.focus();
                  };

                  var parent;
                  try {
                    parent = $window.parent;
                  } catch (e) {
                    // Happens in case of cross domain iframe integration
                  }

                  if (!parent || parent === $window.self) {
                    return $('html, body').animate({
	                    scrollTop: positionElement.top - 10
	                  }, 'slow', function() {
	                    giveFocus();
	                  });
                  }

                  if (!$window.frameElement) {
                    return $log.warning('forms - validationForm - Iframe element is not named, its offset can not be used to manage scroll');
                  }

                  var globalOffset = $($window.frameElement).offset().top + positionElement.top;

                  // 160 est arbitraire et correspond surtout à l'intégration dans portail-acteurs-eco
                  // TODO: meilleure manière de faire ?
                  parent.scrollTo(0, globalOffset - 160 > 0 ? globalOffset - 160 : 0);
                  giveFocus();
                }
              }

              // Exécute la fonction de formulaire invalide
              if (fnI) {
                fnI(scope, {
                  $event: event
                });
              }
            }
          }
          scope.$apply(formSubmit);
        });

        scope.$on('$destroy', function() {
          _.each(watches, function(watch) {
            watch();
          });
        });
      }
    };
  }
]);

angular.module("form.templates", []).run(["$templateCache", function($templateCache) {$templateCache.put("form/form-directives/bool-field/bool-field.html","<fieldset class=\"bool-radio-field\">\n  <legend class=\"sr-only\">{{ namespace + \'.\' + name + \'.label\' | translate }}</legend>\n  <validation-field configuration=\"configuration\" namespace=\"namespace\" bare=\"bare\" label-class=\"labelClass\" group-inputs=\"true\">\n    <label class=\"radio-inline\" for=\"{{name}}-yes\">\n      <input id=\"{{name}}-yes\" type=\"radio\" ng-disabled=\"readOnly\" dynamic-name=\"name\" ng-model=\"data.model\" ng-value=\"valueTrue === undefined ? true : valueTrue\" ng-required=\"!remoteValidation && !configuration.hidden && configuration.required\" ng-change=\"change()\">\n        &nbsp;\n        <span ng-if=\"configuration.valueTrue\" ng-bind-html=\"configuration.valueTrue\"></span>\n        <span ng-if=\"!configuration.valueTrue && (namespace + \'.\' + name + \'.yes\'|translate) != namespace + \'.\' + name + \'.yes\'\">{{namespace + \'.\' + name + \'.yes\'|translate}}</span>\n        <span ng-if=\"!configuration.valueTrue && (namespace + \'.\' + name + \'.yes\'|translate) == namespace + \'.\' + name + \'.yes\'\">Oui</span>\n    </label>\n    <label class=\"radio-inline\" for=\"{{name}}-no\">\n      <input id=\"{{name}}-no\" type=\"radio\" ng-disabled=\"readOnly\" dynamic-name=\"name\" ng-model=\"data.model\" ng-value=\"valueFalse === undefined ? false : valueFalse\" ng-required=\"!remoteValidation && !configuration.hidden && configuration.required\" ng-change=\"change()\">\n        &nbsp;\n        <span ng-if=\"configuration.valueFalse\" ng-bind-html=\"configuration.valueFalse\"></span>\n        <span ng-if=\"!configuration.valueFalse && (namespace + \'.\' + name + \'.no\'|translate) != namespace + \'.\' + name + \'.no\'\">{{namespace + \'.\' + name + \'.no\'|translate}}</span>\n        <span ng-if=\"!configuration.valueFalse && (namespace + \'.\' + name + \'.no\'|translate) == namespace + \'.\' + name + \'.no\'\">Non</span>\n        &nbsp;&nbsp;\n    </label>\n  </validation-field>\n</fieldset>\n");
$templateCache.put("form/form-directives/datetime-field/datetime-field.html","<div class=\"datetime-field\">\n  <validation-field ng-if=\"forceRefresh\" configuration=\"configuration\" namespace=\"namespace\" bare=\"bare\" label-class=\"labelClass\">\n    <input kendo-date-time-picker\n           type=\"text\"\n           ng-disabled=\"readOnly\"\n           dynamic-name=\"name\"\n           k-options=\"dateField.datePickerConfig\"\n           ng-model=\"dateField.ngModel\"           \n           placeholder=\"{{configuration.placeholder || (namespace + \'.\' + name + \'.placeholder\' | tryTranslate)}}\"\n           ng-required=\"!remoteValidation && !configuration.hidden && configuration.required\"\n           check-validation-field=\"validateDateField()\" />\n  </validation-field>\n</div>\n");
$templateCache.put("form/form-directives/date-field/date-field.html","<div class=\"date-field\">\n  <validation-field ng-if=\"forceRefresh\" configuration=\"configuration\" namespace=\"namespace\" bare=\"bare\" label-class=\"labelClass\">\n    <input kendo-date-picker\n           type=\"text\"\n           ng-disabled=\"readOnly\"\n           dynamic-name=\"name\"\n           k-options=\"dateField.datePickerConfig\"\n           k-ng-model=\"dateField.kModel\"\n           ng-model=\"dateField.displayModel\"\n           placeholder=\"{{configuration.placeholder || (namespace + \'.\' + name + \'.placeholder\' | tryTranslate)}}\"\n           ng-required=\"!remoteValidation && !configuration.hidden && configuration.required\"\n           check-validation-field=\"validateDateField()\" />\n  </validation-field>\n</div>\n");
$templateCache.put("form/form-directives/dynamic-field/dynamic-field.html","<div ng-if=\"!dynamicFieldConfiguration.hidden\">\n    <bool-field\n        ng-if=\"regTest(dynamicFieldConfiguration.dataType, \'^(xs:)?boolean$\')\"\n        name=\"{{name}}\"\n        model=\"data.model\"\n        configuration=\"dynamicFieldConfiguration\"\n        view-configuration=\"viewConfiguration\"\n        namespace=\"namespace\"\n        bare=\"bare\"\n        read-only=\"readOnly\"\n        label-class=\"labelClass\">\n    </bool-field>\n\n    <siret-field\n        ng-if=\"regTest(dynamicFieldConfiguration.dataType, \'^(xs:)?string$\') &&\n            (regTest(dynamicFieldConfiguration.subType, \'^(urn:)?siret$\') ||\n            regTest(dynamicFieldConfiguration.subType.uri, \'^(urn:)?siret$\'))\"\n        name=\"{{name}}\"\n        model=\"data.model\"\n        configuration=\"dynamicFieldConfiguration\"\n        view-configuration=\"viewConfiguration\"\n        namespace=\"namespace\"\n        bare=\"bare\"\n        label-class=\"labelClass\"\n        as-string=\"true\"\n        read-only=\"readOnly\">\n    </siret-field>\n\n    <select-field\n        ng-if=\"selectField\"\n        name=\"{{name}}\"\n        model=\"data.model\"\n        configuration=\"dynamicFieldConfiguration\"\n        view-configuration=\"viewConfiguration\"\n        namespace=\"namespace\"\n        source=\"source\"\n        type=\"{{presentation.type}}\"\n        bare=\"bare\"\n        label-class=\"labelClass\"\n        read-only=\"readOnly\">\n    </select-field>\n\n    <!-- Display text fields only once dataSchemaConfiguration is loaded -->\n    <div ng-if=\"regTest(dynamicFieldConfiguration.dataType, \'^(xs:)?string$\') && (dataSchemaConfiguration || !dynamicFieldConfiguration.schema.href)\">\n        <text-field\n            ng-if=\"regTest(dynamicFieldConfiguration.dataType, \'^(xs:)?string$\')\n            && !dynamicFieldConfiguration.subType\n            && !dynamicFieldConfiguration.dataURL\n            && !restrictionDataUrl\n            && !presentation.multiline\n            && !dataSchemaConfiguration.multiLines\n            && !dataSchemaConfiguration.subType.uri\"\n            name=\"{{name}}\"\n            model=\"data.model\"\n            format=\"{{presentation.format || dataSchemaConfiguration.format}}\"\n            configuration=\"dataSchemaConfiguration || dynamicFieldConfiguration\"\n            namespace=\"namespace\"\n            bare=\"bare\"\n            view-configuration=\"viewConfiguration\"\n            label-class=\"labelClass\"\n            read-only=\"readOnly\">\n        </text-field>\n\n        <textarea-field\n            ng-if=\"regTest(dynamicFieldConfiguration.dataType, \'^(xs:)?string$\')\n            && !dynamicFieldConfiguration.subType\n            && !dynamicFieldConfiguration.dataURL\n            && !restrictionDataUrl\n            && (presentation.multiline || dataSchemaConfiguration.multiLines)\"\n            name=\"{{name}}\"\n            model=\"data.model\"\n            format=\"{{presentation.format || dynamicFieldConfiguration.format}}\"\n            configuration=\"dataSchemaConfiguration || dynamicFieldConfiguration\"\n            namespace=\"namespace\"\n            bare=\"bare\"\n            view-configuration=\"viewConfiguration\"\n            label-class=\"labelClass\"\n            read-only=\"readOnly\">\n        </textarea-field>\n    </div>\n\n    <!-- dateOnly flag set to true when type not set to dateTime -->\n    <date-field\n        ng-if=\"regTest(dynamicFieldConfiguration.dataType, \'^(xs:)?date$\') && !dynamicFieldConfiguration.subType\"\n        name=\"{{name}}\"\n        model=\"data.model\"\n        configuration=\"dynamicFieldConfiguration\"\n        view-configuration=\"viewConfiguration\"\n        namespace=\"namespace\"\n        bare=\"bare\"\n        label-class=\"labelClass\"\n        read-only=\"readOnly\"\n        date-only=\"true\">\n    </date-field>\n\n    <date-field\n        ng-if=\"regTest(dynamicFieldConfiguration.dataType, \'^(xs:)?dateTime$\') && !dynamicFieldConfiguration.subType\"\n        name=\"{{name}}\"\n        model=\"data.model\"\n        configuration=\"dynamicFieldConfiguration\"\n        view-configuration=\"viewConfiguration\"\n        namespace=\"namespace\"\n        bare=\"bare\"\n        label-class=\"labelClass\"\n        read-only=\"readOnly\">\n    </date-field>\n\n    <exercice-field\n        ng-if=\"regTest(dynamicFieldConfiguration.dataType, \'^(xs:)?date(Time)?$\') && (regTest(dynamicFieldConfiguration.subType, \'^(urn:)?exercice$\') || regTest(dynamicFieldConfiguration.subType.uri, \'^(urn:)?exercice$\'))\"\n        name=\"{{name}}\"\n        model=\"data.model\"\n        configuration=\"dynamicFieldConfiguration\"\n        view-configuration=\"viewConfiguration\"\n        namespace=\"namespace\"\n        bare=\"bare\"\n        label-class=\"labelClass\"\n        read-only=\"readOnly\">\n    </exercice-field>\n\n    <number-field\n        ng-if=\"regTest(dynamicFieldConfiguration.dataType, \'^(xs:)?integer$\') && !dynamicFieldConfiguration.subType\"\n        name=\"{{name}}\"\n        model=\"data.model\"\n        configuration=\"dynamicFieldConfiguration\"\n        view-configuration=\"viewConfiguration\"\n        namespace=\"namespace\"\n        type=\"n\"\n        subdigits=\"0\"\n        bare=\"bare\"\n        label-class=\"labelClass\"\n        read-only=\"readOnly\">\n    </number-field>\n\n    <number-field\n        ng-if=\"regTest(dynamicFieldConfiguration.dataType, \'^(xs:)?decimal$\') && !dynamicFieldConfiguration.subType\"\n        name=\"{{name}}\"\n        model=\"data.model\"\n        configuration=\"dynamicFieldConfiguration\"\n        view-configuration=\"viewConfiguration\"\n        namespace=\"namespace\"\n        type=\"n\"\n        subdigits=\"{{presentation.subdigits !== undefined ? presentation.subdigits : 2}}\"\n        bare=\"bare\"\n        label-class=\"labelClass\"\n        read-only=\"readOnly\">\n    </number-field>\n\n    <number-field\n        ng-if=\"regTest(dynamicFieldConfiguration.dataType, \'^(xs:)?integer$\') && dynamicFieldConfiguration.subType == \'currency\'\"\n        name=\"{{name}}\"\n        model=\"data.model\"\n        configuration=\"dynamicFieldConfiguration\"\n        view-configuration=\"viewConfiguration\"\n        namespace=\"namespace\"\n        type=\"c\"\n        subdigits=\"0\"\n        bare=\"bare\"\n        label-class=\"labelClass\"\n        read-only=\"readOnly\">\n    </number-field>\n\n    <number-field\n        ng-if=\"regTest(dynamicFieldConfiguration.dataType, \'^(xs:)?decimal$\') && dynamicFieldConfiguration.subType == \'currency\'\"\n        name=\"{{name}}\"\n        model=\"data.model\"\n        configuration=\"dynamicFieldConfiguration\"\n        view-configuration=\"viewConfiguration\"\n        namespace=\"namespace\"\n        type=\"c\"\n        subdigits=\"{{presentation.subdigits !== undefined ? presentation.subdigits : 2}}\"\n        bare=\"bare\"\n        label-class=\"labelClass\"\n        read-only=\"readOnly\">\n    </number-field>\n\n    <number-field\n        ng-if=\"regTest(dynamicFieldConfiguration.dataType, \'^(xs:)?decimal$\') && regTest(dynamicFieldConfiguration.subType.uri, \'^(https?://)?www.currency-iso.org\')\"\n        name=\"{{name}}\"\n        model=\"data.model\"\n        configuration=\"dynamicFieldConfiguration\"\n        view-configuration=\"viewConfiguration\"\n        namespace=\"namespace\"\n        type=\"c\"\n        subdigits=\"{{presentation.subdigits !== undefined ? presentation.subdigits : 2}}\"\n        bare=\"bare\"\n        label-class=\"labelClass\"\n        read-only=\"readOnly\">\n    </number-field>\n\n    <!-- COMPATIBLE WITH DATA SCHEMA -->\n    <!-- YEAR -->\n    <exercice-field\n        ng-if=\"regTest(dynamicFieldConfiguration.dataType, \'^(xs:)?date(Time)?$\') && dynamicFieldConfiguration.subType.uri == \'https://www.w3.org/TR/xmlschema-2/#gYear\'\"\n        name=\"{{name}}\"\n        model=\"data.model\"\n        configuration=\"dynamicFieldConfiguration\"\n        view-configuration=\"viewConfiguration\"\n        namespace=\"namespace\"\n        bare=\"bare\"\n        label-class=\"labelClass\"\n        read-only=\"readOnly\">\n    </exercice-field>\n\n    <!-- NAF -->\n    <select-field\n        ng-if=\"dynamicFieldConfiguration.subType.uri == \'http://xml.insee.fr/schema/naf-enum.html#SousClasseNAF2008_stype\'\"\n        type=\"typeahead\"\n        name=\"{{name}}\"\n        model=\"data.model\"\n        configuration=\"dynamicFieldConfiguration\"\n        view-configuration=\"viewConfiguration\"\n        namespace=\"namespace\"\n        source=\"source\"\n        bare=\"bare\"\n        label-class=\"labelClass\"\n        read-only=\"readOnly\">\n    </select-field>\n\n    <!-- SIRET -->\n    <siret-field\n        ng-if=\"regTest(dynamicFieldConfiguration.dataType, \'^(xs:)?string$\') && dynamicFieldConfiguration.subType.uri == \'http://xml.insee.fr/schema/siret.html#SIRETStructure_ctype\'\"\n        name=\"{{name}}\"\n        model=\"data.model\"\n        configuration=\"dynamicFieldConfiguration\"\n        view-configuration=\"viewConfiguration\"\n        namespace=\"namespace\"\n        bare=\"bare\"\n        label-class=\"labelClass\"\n        as-string=\"true\"\n        read-only=\"readOnly\">\n    </siret-field>\n\n    <!-- DATA-SCHEMA ENUMERATION FIELD -->\n    <validation-field\n      ng-if=\"regTest(dynamicFieldConfiguration.dataType, \'^(xs:)?enumeration\')\"\n      configuration=\"dynamicFieldConfiguration\"\n      namespace=\"namespace\"\n      label-class=\"labelClass\">\n      <input type=\"text\" dynamic-name=\"name\" ng-show=\"false\" ng-required=\"isRequired()\" ng-model=\"data.model\" ng-disabled=\"readOnly\" />\n      <select\n        name=\"{{name}}\"\n        class=\"form-control\"\n        ng-model=\"data.model\"\n        ng-options=\"item as item.title for item in dynamicFieldConfiguration.restrictions[0].enumeration.item track by item.value\"\n        ng-disabled=\"readOnly\"\n        ng-required=\"isRequired()\">\n        <option value=\"\">{{configuration.placeholder || (namespace + \'.\' + name + \'.placeholder\' | tryTranslate)}}</option>\n      </select>\n    </validation-field>\n\n    <!-- dynamic typahead -->\n    <validation-field\n      ng-if=\"dataSchemaConfiguration.displayKey && dataSchemaConfiguration.searchKey\"\n      configuration=\"dynamicFieldConfiguration\"\n      namespace=\"namespace\"\n      label-class=\"labelClass\">\n        <input autocomplete=\"off\" type=\"text\" dynamic-name=\"name\"\n        class=\"form-control\" ng-model=\"data.model\"\n        placeholder=\"{{configuration.placeholder || (namespace + \'.\' + name + \'.placeholder\' | tryTranslate)}}\" typeahead=\"{{ngOptions}}\"\n        typeahead-wait-ms=\"100\" ng-required=\"isRequired()\" ng-disabled=\"readOnly\"\n        typeahead-editable=\"false\" typeahead-on-select=\"typeaheadOnSelect($item)\" ng-change=\"onChange()\">\n    </validation-field>\n\n    <!-- MDM PAYS -->\n    <select-field\n        ng-if=\"dynamicFieldConfiguration.subType.uri == \'http://www.iso.org/iso/fr/home/standards/country_codes/updates_on_iso_3166.htm\'\"\n        type=\"typeahead\"\n        name=\"{{name}}\"\n        model=\"data.model\"\n        configuration=\"dynamicFieldConfiguration\"\n        view-configuration=\"viewConfiguration\"\n        namespace=\"namespace\"\n        source=\"source\"\n        bare=\"bare\"\n        label-class=\"labelClass\"\n        read-only=\"readOnly\">\n    </select-field>\n\n    <!-- DECIMAL CURRENCY -->\n    <number-field\n        ng-if=\"regTest(dynamicFieldConfiguration.dataType, \'^(xs:)?decimal$\') && dynamicFieldConfiguration.subType.uri == \'urn:mgdis:subtypes:currencyISO4217\'\"\n        name=\"{{name}}\"\n        model=\"data.model\"\n        configuration=\"dynamicFieldConfiguration\"\n        view-configuration=\"viewConfiguration\"\n        namespace=\"namespace\"\n        type=\"c\"\n        subdigits=\"{{presentation.subdigits !== undefined ? presentation.subdigits : 2}}\"\n        bare=\"bare\"\n        label-class=\"labelClass\"\n        read-only=\"readOnly\">\n    </number-field>\n</div>\n");
$templateCache.put("form/form-directives/dynamic-form-groups/dynamic-form-groups.html","<div class=\"dynamic-form-groups\">\n\n    <fieldset ng-if=\"!group.configuration.hidden\" ng-repeat=\"group in presentation.groups\" ng-class=\"{nolegend: !group.legend}\">\n\n        <legend ng-if=\"group.legend\">\n      <i class=\"fa fa-circle\" aria-hidden=\"true\"></i>&nbsp;\n            {{group.legend}}\n        </legend>\n\n        <blockquote ng-if=\"group.header\">\n      {{group.header}}\n    </blockquote>\n\n    <dynamic-field ng-repeat=\"field in group.fields\"\n        name=\"{{field}}\"\n        model=\"data[field]\"\n        configuration=\"fields[field]\"\n        namespace=\"namespace\"\n        mdm=\"mdm\"\n        presentation=\"presentation.fields[field]\"\n      read-only=\"readOnly\"\n            auth-user=\"authUser\"\n            auth-pass=\"authPass\"\n            view-configuration=\"viewConfiguration\"\n            label-class=\"fields[field].labelClass\">\n    </dynamic-field>\n\n    <blockquote ng-if=\"group.footer\">\n      {{group.footer}}\n    </blockquote>\n\n    </fieldset>\n</div>\n");
$templateCache.put("form/form-directives/editable-grid/editable-grid.html","<div class=\"editable-grid\">\n    <kendo-grid options=\"gridOptions\"></kendo-grid>\n</div>");
$templateCache.put("form/form-directives/exercice-field/exercice-field.html","<div class=\"exercice-field\">\n  <validation-field ng-if=\"forceRefresh\" configuration=\"configuration\" namespace=\"namespace\" bare=\"bare\" label-class=\"labelClass\">\n    <input kendo-date-picker type=\"text\"\n           dynamic-name=\"name\"\n           k-options=\"exerciceField.datePickerConfig\"\n           ng-model=\"exerciceField.ngModel\"\n           k-min=\"exerciceField.datePickerConfig.min\"\n           k-max=\"exerciceField.datePickerConfig.max\"\n           ng-disabled=\"readOnly\"\n           placeholder=\"{{getPlaceholder()}}\"\n           ng-required=\"!remoteValidation && !configuration.hidden && configuration.required\"\n           check-validation-field=\"validateExerciceField()\"/>\n  </validation-field>\n</div>");
$templateCache.put("form/form-directives/file-field/file-field.html","<div validation-field namespace=\"namespace\" configuration=\"configuration\" bare=\"bare\">\n  <file-input \n    name=\"{{name}}\"\n    model=\"model\"\n    label=\"{{namespace + \'.\' + name + \'.button\' | translate}}\"\n    placeholder=\"{{configuration.placeholder || (namespace + \'.\' + name + \'.placeholder\' | tryTranslate)}}\"\n    required=\"!remoteValidation && !configuration.hidden && configuration.required\"\n    multiple=\"configuration.multiple\">\n  </file-input>\n</div>");
$templateCache.put("form/form-directives/file-field/file-input.html","<div class=\"file-input input-group\">\n  <span class=\"btn btn-default btn-file\">\n    {{label}}\n    <input dynamic-name=\"name\" type=\"file\" class=\"file\" ng-model=\"names\" ng-required=\"required\">\n  </span><br ng-if=\"names\"/>\n  <span>{{ names || placeholder }}</span>\n  <br ng-if=\"fileErrors\"/>\n  <div class=\"has-error\" ng-if=\"fileErrors\">\n    <span class= \"help-block\" >{{ fileErrors }}</span>\n  </div>\n</div>\n\n");
$templateCache.put("form/form-directives/number-field/number-field.html","<div class=\"number-field\">\n  <validation-field bare=\"bare\" configuration=\"configuration\" field-width=\"fieldWidth\" help-width=\"helpWidth\"\n                    label-class=\"labelClass\" label-width=\"labelWidth\"\n                    namespace=\"namespace\">\n    <input dynamic-name=\"name\"\n           k-decimals=\"numberField.decimals\"\n           k-ng-model=\"numberField.modelValue\"\n           k-options=\"buildKOptions()\"\n           kendo-numeric-text-box\n           maxlength=\"{{getMaxlength(configuration.maxlength)}}\"\n           minlength=\"{{getMinlength(configuration.minlength)}}\"\n           ng-disabled=\"readOnly\"\n           ng-model=\"numberField.displayValue\"\n           ng-required=\"!remoteValidation && !configuration.hidden && configuration.required\"\n           aria-describedby=\"{{name}}-help {{name}}-errors {{name}}-details\"\n    />\n  </validation-field>\n</div>\n");
$templateCache.put("form/form-directives/select-field/select-field.html","<div class=\"select-field\" ng-switch=\"type\">\n  <div ng-switch-default>\n    <validation-field class=\"select-field-select\" configuration=\"configuration\" namespace=\"namespace\" bare=\"bare\" label-class=\"labelClass\"\n                      label-width=\"labelWidth\" field-width=\"fieldWidth\" help-width=\"helpWidth\">\n      <span class=\"field-loader\" ng-show=\"configuration.loading\"><i class=\"fa fa-refresh fa-spin\" aria-hidden=\"true\"></i></span>\n      <select\n        class=\"form-control\"\n        dynamic-name=\"name\"\n        ng-model=\"data.model\"\n        ng-options=\"item as item.expand.libelle.value for item in source.array track by item.expand.reference\"\n        ng-required=\"!remoteValidation && !configuration.hidden && configuration.required\"\n        ng-disabled=\"configuration.loading || readOnly\"\n        ng-change=\"change()\"\n        options-attributes=\"tooltips && {title: \'expand.libelle.value\'}\">\n        <option value=\"\">{{configuration.placeholder || (namespace + \'.\' + name + \'.placeholder\' | tryTranslate)}}</option>\n      </select>\n    </validation-field>\n  </div>\n  <div ng-switch-when=\"typeahead\">\n      <validation-field class=\"select-field-typeahead\" configuration=\"configuration\" namespace=\"namespace\" bare=\"bare\" label-class=\"labelClass\"\n                        label-width=\"labelWidth\" field-width=\"fieldWidth\" help-width=\"helpWidth\">\n      <span class=\"field-loader\" ng-show=\"configuration.loading\"><i class=\"fa fa-refresh fa-spin\" aria-hidden=\"true\"></i></span>\n      <input\n        autocomplete=\"off\"\n        dynamic-name=\"name\"\n        class=\"form-control\"\n        ng-model=\"data.model\"\n        ng-required=\"!remoteValidation && !configuration.hidden && configuration.required\"\n        placeholder=\"{{configuration.placeholder || (namespace + \'.\' + name + \'.placeholder\' | tryTranslate)}}\"\n        uib-typeahead=\"item as item.expand.libelle.value for item in source.array | filter:{expand:{libelle:{value:$viewValue}}} | limitTo:limit\"\n        typeahead-editable=\"false\"\n        typeahead-on-select=\"change()\"\n        ng-disabled=\"configuration.loading || readOnly\">\n    </validation-field>\n  </div>\n</div>\n");
$templateCache.put("form/form-directives/siret-field/siret-field.html","<fieldset\n  class=\"form-group siret row\"\n  ng-class=\"{\'has-error\' :\n        (isFieldInvalid(\'SIREN\') && (status.SIREN===\'error\' && form[name + \'-SIREN\'].$dirty || form.$submitted || viewConfiguration.showErrors)) ||\n        (isFieldInvalid(\'NIC\') && (status.NIC===\'error\' && form[name + \'-NIC\'].$dirty || form.$submitted || viewConfiguration.showErrors)) ||\n        (forbiddenSIRET && isSIRETForbidden(SIRET.SIREN, SIRET.NIC))}\"\n  role=\"group\"\n  aria-labelledby=\"{{name}}-label\"\n>\n  <legend\n    id=\"{{name}}-label\"\n    class=\"control-label col-{{colType}}-{{labelWidth}} {{labelClass}}\"\n  >\n    <span>{{label}}</span>&nbsp;<span\n      class=\"asterisque\"\n      ng-if=\"configuration.required\"\n      >*</span\n    >\n  </legend>\n\n  <div\n    class=\"col-{{colType}}-{{fieldWidth}}\"\n    ng-class=\"{\'col-xs-11\' : help && helpTooltip}\"\n  >\n    <div class=\"siret__siren-nic-container\">\n      <!-- SIREN part -->\n      <div class=\"siret__siren\">\n        <!-- SIREN input -->\n        <label for=\"{{ name + \'-SIREN\' }}\" class=\"sr-only\">SIREN</label>\n        <input\n          type=\"text\"\n          dynamic-name=\"name + \'-SIREN\'\"\n          class=\"form-control\"\n          ng-model=\"SIRET.SIREN\"\n          ng-model-options=\"{allowInvalid:\'true\'}\"\n          maxlength=\"9\"\n          ng-blur=\"status.SIREN = isFieldInvalid(\'SIREN\') ? \'error\': \'success\'\"\n          ng-pattern=\"/^[0-9]{9}$/\"\n          ng-paste=\"pasteSIREN($event)\"\n          ng-required=\"!remoteValidation && !configuration.hidden && configuration.required\"\n          ng-disabled=\"readOnly\"\n          ng-change=\"dirty = true\"\n          title=\"SIREN\"\n          aria-describedby=\"{{name}}-SIREN-errors {{name}}-forbiddenSIRET {{name}}-SIREN-format-help {{name}}-SIRET-validity {{labels.details ? name + \'details\' : \'\'}} {{help && !helpTooltip ? name + \'help\' : \'\'}}\"\n        />\n\n        <!-- SIREN format help -->\n        <span id=\"{{name}}-SIREN-format-help\" class=\"siret__format-help\"\n          >{{ \'form.field.formatHelp.SIRET.SIREN\' | translate }}</span\n        >\n\n        <!-- SIREN errors -->\n        <span id=\"{{name}}-SIREN-errors\" class=\"error-container\">\n          <span\n            ng-if=\"hasErrorRequired(\'SIREN\')\n                                && (status.SIREN===\'error\' && (form[name + \'-SIREN\'].$dirty || form.$submitted) || viewConfiguration.showErrors)\"\n            class=\"help-block\"\n          >\n            <span\n              ng-if=\"(namespace + \'.SIREN.error.required\' | translate) !== namespace + \'.SIREN.error.required\'\"\n            >\n              {{namespace + \'.SIREN.error.required\'| translate}}\n            </span>\n            <span\n              ng-if=\"(namespace + \'.SIREN.error.required\' | translate) === namespace + \'.SIREN.error.required\'\"\n            >\n              {{\'form.field.error.SIRET.SIREN.required\' | translate}}\n            </span>\n          </span>\n          <span\n            ng-if=\"!hasErrorRequired(\'SIREN\')\n                                && form[name + \'-SIREN\'].$error.pattern\n                                && (status.SIREN===\'error\' && form[name + \'-SIREN\'].$dirty || form.$submitted)\"\n            class=\"help-block\"\n          >\n            <span\n              ng-if=\"(namespace + \'.SIREN.error.pattern\' | translate) !== namespace + \'.SIREN.error.pattern\'\"\n            >\n              {{namespace + \'.SIREN.error.pattern\'| translate}}\n            </span>\n            <span\n              ng-if=\"(namespace + \'.SIREN.error.pattern\' | translate) === namespace + \'.SIREN.error.pattern\'\"\n            >\n              {{\'form.field.error.SIRET.SIREN.pattern\' | translate}}\n            </span>\n          </span>\n        </span>\n      </div>\n\n      <!-- NIC part -->\n      <div class=\"nic\">\n        <!-- NIC input -->\n        <label for=\"{{ name + \'-NIC\' }}\" class=\"sr-only\">NIC</label>\n        <input\n          type=\"text\"\n          dynamic-name=\"name + \'-NIC\'\"\n          class=\"form-control\"\n          ng-model=\"SIRET.NIC\"\n          ng-model-options=\"{allowInvalid:\'true\'}\"\n          maxlength=\"5\"\n          ng-blur=\"status.NIC = isFieldInvalid(\'NIC\') ? \'error\': \'success\'\"\n          check-validation-field=\"checkSiretValidity(SIRET.SIREN, SIRET.NIC)\"\n          ng-pattern=\"/^[0-9]{5}$/\"\n          ng-paste=\"status.NIC = form[name + \'-NIC\'].$invalid ? \'error\': \'success\'\"\n          ng-required=\"!remoteValidation && !configuration.hidden && configuration.required\"\n          ng-disabled=\"readOnly && !edit\"\n          ng-change=\"dirty = true\"\n          aria-describedby=\"{{name}}-NIC-errors {{name}}-forbiddenSIRET {{name}}-format-help {{name}}-SIRET-validity {{labels.details ? name + \'details\' : \'\'}} {{help && !helpTooltip ? name + \'help\' : \'\'}}\"\n        />\n\n        <!-- NIC format help -->\n        <span id=\"{{name}}-SIREN-format-help\" class=\"siret__format-help\"\n          >{{ \'form.field.formatHelp.SIRET.NIC\' | translate }}</span\n        >\n\n        <!-- NIC errors -->\n        <span id=\"{{name}}-NIC-errors\" class=\"error-container\">\n          <span\n            ng-if=\"hasErrorRequired(\'NIC\')\n                                && (status.NIC===\'error\' && (form[name + \'-NIC\'].$dirty || form.$submitted) || viewConfiguration.showErrors)\"\n            class=\"help-block\"\n          >\n            <span\n              ng-if=\"(namespace + \'.NIC.error.required\' | translate) !== namespace + \'.NIC.error.required\'\"\n            >\n              {{namespace + \'.NIC.error.required\'| translate}}\n            </span>\n            <span\n              ng-if=\"(namespace + \'.NIC.error.required\' | translate) === namespace + \'.NIC.error.required\'\"\n            >\n              {{\'form.field.error.SIRET.NIC.required\' | translate}}\n            </span>\n          </span>\n          <span\n            ng-if=\"!hasErrorRequired(\'NIC\')\n                                && form[name + \'-NIC\'].$error.pattern\n                                && (status.NIC===\'error\' && form[name + \'-NIC\'].$dirty || form.$submitted)\"\n            class=\"help-block\"\n          >\n            <span\n              ng-if=\"(namespace + \'.NIC.error.pattern\' | translate) !== namespace + \'.NIC.error.pattern\'\"\n            >\n              {{namespace + \'.NIC.error.pattern\'| translate}}\n            </span>\n            <span\n              ng-if=\"(namespace + \'.NIC.error.pattern\' | translate) === namespace + \'.NIC.error.pattern\'\"\n            >\n              {{\'form.field.error.SIRET.NIC.pattern\' | translate}}\n            </span>\n          </span>\n        </span>\n      </div>\n    </div>\n\n    <span\n      ng-if=\"!hasErrorRequired(\'NIC\')\n                          && !form[name + \'-NIC\'].$error.pattern\n                          && form[name + \'-NIC\'].$error.checkValidity\n                          && status.NIC===\'error\'\n                          && (form[name + \'-NIC\'].$dirty || form.$submitted)\"\n      id=\"{{name}}-SIRET-validity\"\n      class=\"help-block\"\n    >\n      <span\n        ng-if=\"(namespace + \'.NIC.error.checkValidity\' | translate) !== namespace + \'.NIC.error.checkValidity\'\"\n      >\n        {{namespace + \'.NIC.error.checkValidity\'| translate}}\n      </span>\n      <span\n        ng-if=\"(namespace + \'.NIC.error.checkValidity\' | translate) === namespace + \'.NIC.error.checkValidity\'\"\n      >\n        {{\'form.field.error.SIRET.checkValidity\' | translate}}\n      </span>\n    </span>\n\n    <span class=\"help-block\" id=\"{{name}}-forbiddenSIRET\">\n      <span\n        ng-if=\"forbiddenSIRET && isSIRETForbidden(SIRET.SIREN, SIRET.NIC) && forbiddenSIRETLabel\"\n      >\n        {{forbiddenSIRETLabel}}\n      </span>\n      <span\n        ng-if=\"forbiddenSIRET && isSIRETForbidden(SIRET.SIREN, SIRET.NIC) && !forbiddenSIRETLabel\"\n      >\n        {{\'form.field.error.SIRET.forbiddenSIRET\' | translate}}\n      </span>\n    </span>\n\n    <span\n      ng-if=\"labels.details\"\n      class=\"help-block form-details\"\n      ng-bind-html=\"labels.details\"\n      id=\"{{name}}-details\"\n    ></span>\n  </div>\n\n  <span\n    ng-if=\"help && helpTooltip\"\n    bs-tooltip\n    data-html=\"true\"\n    data-title=\"{{help || labels.help}}\"\n    data-placement=\"left\"\n    tooltip-trigger=\"\'mouseenter focus\'\"\n    tabindex=\"0\"\n    class=\"siret-field-help-tooltip col-xs-1\"\n  >\n    <span id=\"{{name}}-help\" class=\"sr-only\">{{help || labels.help}}</span>\n    <span class=\"fa fa-info-circle fa-lg\" aria-hidden=\"true\"></span>\n  </span>\n\n  <div\n    ng-if=\"help && !helpTooltip\"\n    class=\"siret-field-help validation-field-help col-{{colType}}-{{helpWidth}}\"\n    id=\"{{name}}-help\"\n  >\n    <span ng-if=\"!helpTooltip\">{{help || labels.help}}</span>\n  </div>\n</fieldset>\n");
$templateCache.put("form/form-directives/stepswizard/stepswizard.html","<div class=\"stepwizard\">\n  <div class=\"stepwizard-row\">\n    <div class=\"stepwizard-step\" ng-repeat=\"(key, step) in steps\">\n      <button\n        type=\"button\"\n        id=\"{{ \'step-wizard-btn-\' + key }}\"\n        class=\"btn btn-default btn-circle\"\n        ng-class=\"{\'btn-primary\': isStepActive($index, step) }\"\n        aria-label=\"{{getStepTitle($index, step)}}\"\n        disabled\n      >\n        {{$index + 1}}\n      </button>\n      <p>\n        <strong ng-if=\"useCustomNames\">{{ customStepsNames[key] }}</strong>\n        <strong ng-if=\"!useCustomNames\"\n          >{{ labelsPrefix + \'.\' + step | translate }}</strong\n        >\n      </p>\n    </div>\n  </div>\n</div>\n");
$templateCache.put("form/form-directives/tags-field/tags-field.html","<div>\n    <validation-field configuration=\"configuration\" namespace=\"namespace\" label-class=\"labelClass\">\n    <input dynamic-name=\"name\" ng-model=\"status.filled\" ng-hide=\"true\" ng-required=\"!remoteValidation && !configuration.hidden && configuration.required\">\n    <span class=\"field-loader\" ng-show=\"loadingData\"><i class=\"fa fa-refresh fa-spin\" aria-hidden=\"true\"></i></span>\n\n    <div ng-if=\"autocomplete\">\n      <tags-input ng-disabled=\"loadingData\" ng-model=\"tagsData\" key-property=\"{{keyProperty}}\" display-property=\"{{displayProperty}}\" placeholder=\"{{placeholder | translate }}\" on-tag-adding=\"!readOnly && validTag($tag)\" on-tag-removing=\"!readOnly\" replace-spaces-with-dashes=\"false\">\n        <auto-complete source=\"filterSource(source, $query, displayProperty)\" display-property=\"{{displayProperty}}\" min-length=\"1\" max-results-to-show=\"{{nbResults}}\" load-on-down-arrow=\"true\" />\n      </tags-input>\n    </div>\n\n    <div ng-if=\"!autocomplete\">\n      <tags-input ng-disabled=\"loadingData\" ng-model=\"tagsData\" placeholder=\"{{placeholder | translate }}\" replace-spaces-with-dashes=\"false\" min-length=\"1\" on-tag-adding=\"!readOnly\" on-tag-removing=\"!readOnly\">\n      </tags-input>\n    </div>\n\n    <div class=\"form-group has-error\" ng-if=\"displayInvalidTagMessage && invalidForm && invalidField\">\n      <span class=\"help-block\" ng-bind-html=\"viewConfiguration.ns + \'.\' + name + \'.invalidInput\' | translate\"></span>\n    </div>\n  </validation-field>\n</div>");
$templateCache.put("form/form-directives/text-field/text-field.html","<div class=\"text-field\">\n    <validation-field configuration=\"configuration\" namespace=\"namespace\" bare=\"bare\" label-class=\"labelClass\" base-input-informations=\"name + \'-charactersLeft\'\">\n        <input type=\"text\"\n            class=\"form-control\"\n            format-field format=\"format\"\n            dynamic-name=\"name\"\n            ng-model=\"data.model\"\n            ng-pattern=\"!configuration.hidden && (configuration.restrictions && configuration.restrictions[0] && configuration.restrictions[0].pattern || configuration.pattern)\"\n            ng-required=\"isRequired()\"\n            aria-required=\"{{ isRequired() }}\"\n            minlength=\"{{configuration.minlength}}\"\n            maxlength=\"{{configuration.maxlength}}\"\n            placeholder=\"{{configuration.placeholder || (namespace + \'.\' + name + \'.placeholder\' | tryTranslate)}}\"\n            aria-label=\"{{configuration[\'aria-label\'] || (namespace + \'.\' + name + \'.aria-label\' | tryTranslate)}}\"\n            ng-disabled=\"readOnly\"\n            ng-change=\"change()\"\n            ng-focus=\"charLeft.display = true\"\n            ng-blur=\"waitUndisplayCharacterLeft()\"\n            autocomplete=\"{{autocomplete}}\"\n        />\n        <span id=\"{{name}}-charactersLeft\" ng-show=\"displayCharactersLeft && charLeft.display\">{{ \'form.field.charactersLeft\' | translate:charLeft }}</span>\n    </validation-field>\n</div>\n");
$templateCache.put("form/form-directives/textarea-field/textarea-field.html","<div class=\"textarea-field\">\n  <validation-field ng-if=\"!configuration.richtext\" configuration=\"configuration\" namespace=\"namespace\" bare=\"bare\" label-class=\"labelClass\" base-input-informations=\"name + \'-charactersLeft\'\">\n    <textarea\n      class=\"form-control\"\n      format-field format=\"format\"\n      dynamic-name=\"name\"\n      ng-model=\"data.model\"\n      ng-model-options=\"{ allowInvalid:\'true\'}\"\n      ng-required=\"!remoteValidation && !configuration.hidden && configuration.required\"\n      ng-minlength=\"configuration.restrictions && configuration.restrictions[0] && configuration.restrictions[0].minLength || configuration.minlength || 0\"\n      ng-maxlength=\"maxLength\"\n      minlength=\"{{configuration.restrictions && configuration.restrictions[0] && configuration.restrictions[0].minLength || configuration.minlength || 0}}\"\n      maxlength=\"{{maxLength}}\"\n      placeholder=\"{{configuration.placeholder || (namespace + \'.\' + name + \'.placeholder\' | tryTranslate)}}\"\n      aria-label=\"{{configuration[\'aria-label\'] || (namespace + \'.\' + name + \'.aria-label\' | tryTranslate)}}\"\n      ng-disabled=\"readOnly\"\n      rows=\"{{rows || 2}}\"\n      text-resize-iframe\n      ng-trim=\"false\"\n      ng-focus=\"charLeft.display = true\">\n    </textarea>\n    <span id=\"{{name}}-charactersLeft\" ng-show=\"displayCharactersLeft && charLeft.display\">{{ \'form.field.charactersLeft\' | translate:charactersLeftData }}</span>\n  </validation-field>\n  <validation-field ng-if=\"configuration.richtext\" configuration=\"configuration\" namespace=\"namespace\" bare=\"bare\" label-class=\"labelClass\">\n    <input type=\"text\" dynamic-name=\"name\" ng-show=\"false\" ng-required=\"!remoteValidation && !configuration.hidden && configuration.required\" ng-model=\"data.model\" ng-disabled=\"readOnly\" />\n    <div\n      format-field format=\"format\"\n      ng-model=\"data.model\"\n      k-ng-model=\"data.model\"\n      ng-required=\"!remoteValidation && !configuration.hidden && configuration.required\"\n      placeholder=\"{{configuration.placeholder || (namespace + \'.\' + name + \'.placeholder\' | tryTranslate)}}\"\n      ng-minlength=\"configuration.restrictions && configuration.restrictions[0] && configuration.restrictions[0].minLength || configuration.minlength || 0\"\n      ng-maxlength=\"maxLength\"\n      minlength=\"{{configuration.restrictions && configuration.restrictions[0] && configuration.restrictions[0].minLength || configuration.minlength || 0}}\"\n      maxlength=\"{{maxLength}}\"\n      text-resize-iframe\n\n      kendo-editor\n      k-encoded=\"false\"\n      k-data-bind=\"{\n      value: \'Editor.HTML\',\n      name: \'kendoEditor\'\n      }\"\n      k-tools=\"tools\"\n      k-on-change=\"kendoEditorChange()\"\n      style=\"overflow-y: auto; resize: vertical\">\n    </div>\n  </validation-field>\n</div>\n");
$templateCache.put("form/form-directives/validation-field/field-errors.html","<div>\n  <span ng-if=\"getErrorKey()\" class=\"help-block\">\n    {{ fieldConfiguration.error[getErrorKey()] }}\n  </span>\n</div>\n");
$templateCache.put("form/form-directives/validation-field/validation-field.html","<div\n    ng-show=\"!fieldConfiguration.hidden\"\n    class=\"form-group validation-field row\"\n    data-namespace=\"{{fieldConfiguration.ns}}.{{fieldConfiguration.reference}}\"\n    aria-labelledby=\"{{fieldName}}-label\"\n    role=\"{{fieldConfiguration.groupInputs ? \'group\' : \'presentation\'}}\"\n	ng-class=\"{\'has-error\' : field.$invalid && (displayErrors() || showErrors) || hasRequiredError() && showErrors,\n                   \'has-tooltip\' : fieldConfiguration.help && fieldConfiguration.helpTooltip,\n                   \'has-warning\' : fieldConfiguration.displayIcons && fieldConfiguration.displayWarning && field.$invalid && status!==\'error\' && (field.$dirty || showErrors),\n                   \'has-success\' : fieldConfiguration.displayIcons && field.$valid && (field.$dirty || form.$submitted),\n                   \'has-feedback\' : (fieldConfiguration.displayIcons && field.$valid && (field.$dirty || form.$submitted || showErrors)) || (field.$invalid && displayErrors() || showErrors) || (hasRequiredError() && showErrors) || (fieldConfiguration.displayIcons && fieldConfiguration.displayWarning && (field.$invalid || hasRequiredError()) && status!==\'error\' && field.$dirty)}\">\n\n    <label id=\"{{fieldName}}-label\" for=\"{{fieldConfiguration.labelFor}}\" class=\"control-label col-{{widths.colType}}-{{widths.label}} {{labelClass}} validation-field-label\" ng-class=\"{ \'sr-only\' : fieldConfiguration.bare }\">\n        <span ng-bind-html=\"fieldConfiguration.label\"></span>&nbsp;<span class=\"asterisque\" ng-if=\"required\">*</span>\n    </label>\n\n    <div class=\"col-{{widths.colType}}-{{widths.field}} {{fieldClass}} validation-field-field\">\n\n        <!-- Transcluded input -->\n        <ng-transclude></ng-transclude>\n\n        <span ng-if=\"fieldConfiguration.unit\" class=\"validation-field-unit\">{{fieldConfiguration.unit}}</span>\n        <div ng-if=\"fieldConfiguration.displayIcons\">\n            <span class=\"fa fa-ok form-control-feedback\" ng-if=\"field.$valid && (field.$dirty || form.$submitted || showErrors)\" aria-hidden=\"true\"></span>\n            <span class=\"fa fa-exclamation-triangle form-control-feedback\" ng-if=\"(field.$invalid || hasRequiredError()) && fieldConfiguration.displayWarning && status!==\'error\' && field.$dirty\" aria-hidden=\"true\"></span>\n            <span class=\"fa fa-remove form-control-feedback\" ng-if=\"field.$invalid && (displayErrors() || showErrors) || hasRequiredError() && showErrors\" aria-hidden=\"true\"></span>\n        </div>\n        <span\n            ng-if=\"fieldConfiguration.help && fieldConfiguration.helpTooltip\"\n            data-html=\"true\"\n            data-title=\"{{fieldConfiguration.help}}\"\n            data-placement=\"{{fieldConfiguration.tooltipPlacement || \'left\'}}\"\n            tooltip-trigger=\"\'mouseenter focus\'\"\n            tabindex=\"0\"\n            class=\"validation-field-help-tooltip\"\n            bs-tooltip\n        >\n            <span id=\"{{fieldName}}-help\" class=\"sr-only\">{{fieldConfiguration.help}}</span>\n            <span class=\"fa fa-info-circle fa-lg\" aria-hidden=\"true\"></span>\n        </span>\n\n        <div id=\"{{fieldName}}-errors\" ng-if=\"!fieldConfiguration.errorsInline\" ng-include=\"\'form/form-directives/validation-field/field-errors.html\'\">\n        </div>\n\n        <span id=\"{{fieldName}}-help\" ng-if=\"fieldConfiguration.help && fieldConfiguration.errorsInline && !fieldConfiguration.helpTooltip\">\n            {{fieldConfiguration.help}}\n        </span>\n\n        <span id=\"{{fieldName}}-details\" ng-if=\"fieldConfiguration.details\" class=\"help-block form-details\" ng-bind-html=\"fieldConfiguration.details\"></span>\n\n    </div>\n\n    <div id=\"{{fieldName}}-help\" ng-if=\"!fieldConfiguration.bare && fieldConfiguration.help && !fieldConfiguration.errorsInline && !fieldConfiguration.helpTooltip\" class=\"col-{{widths.colType}}-{{widths.help}} validation-field-help\">\n        <span>{{fieldConfiguration.help}}</span>\n    </div>\n\n    <div id=\"{{fieldName}}-errors\" ng-if=\"!fieldConfiguration.bare && fieldConfiguration.errorsInline\" class=\"col-{{widths.colType}}-{{widths.help}}\">\n        <div ng-include=\"\'form/form-directives/validation-field/field-errors.html\'\"></div>\n    </div>\n</div>");}]);
/**
 * @ngdoc module
 * @module form
 * @name form.services
 * @description
 *
 *   Services related to form management.
 *
 *   This module is a part of {@link ../form/index.html form}.
 *
 *   TODO document the services.
 *
 */

angular.module('form.services', ['configuration']);
angular.module('form.services').factory('dateUtils', [

  function() {
    'use strict';

    /*
     * Set the minimum and maximum limits of a date field
     * @param border {string} - either 'min' or 'max'
     * @param restrictions {array} - List of restrictions objects
     * @param dateField {object} - date field configuration in the scope
     * @param unit {string} - time unit on which operating calculations : days (default), weeks, months or years
     */
    var setBorderRestriction = function(border, restrictions, dateField, unit) {
      unit = unit || 'days';

      // Finding a restriction containing a parameter [min|max]Inclusive or [min|max]Exclusive
      var borderConstraint = _.find(restrictions, function(constraint) {
        return _.has(constraint, border + 'Inclusive') || _.has(constraint, border + 'Exclusive');
      });

      if (borderConstraint) {
        var limit;
        // Inclusive limit mean that we just have to set the date to the kendo directive
        if (_.has(borderConstraint, border + 'Inclusive')) {
          if (unit === 'years' &&
            borderConstraint[border + 'Inclusive'] &&
            borderConstraint[border + 'Inclusive'].toString().length === 4) {
            limit = new Date(borderConstraint[border + 'Inclusive'], 0, 1);
          } else {
            limit = new Date(borderConstraint[border + 'Inclusive']);
          }
        }
        // On exclusive, though, we have to take the date next to the limit
        else if (_.has(borderConstraint, border + 'Exclusive')) {
          var borderMoment;
          if (unit === 'years' && borderConstraint[border + 'Exclusive'] && borderConstraint[border + 'Exclusive'].toString().length === 4) {
            borderMoment = moment(new Date(borderConstraint[border + 'Exclusive'], 0, 1));
          } else {
            borderMoment = moment(new Date(borderConstraint[border + 'Exclusive']));
          }
          // Next [day] for minimum, previous [day] for maximum
          switch (border) {
            case 'min':
              limit = borderMoment.add(1, unit).toDate();
              break;
            case 'max':
              limit = borderMoment.subtract(1, unit).toDate();
              break;
          }
        }

        // Setting the kendo directive's configuration
        dateField.datePickerConfig[border] = limit;
      }
    };

    /**
     * Gets the duration of the given string duration
     * @param  {string} durationString The duration given in a string format
     * example: P5y2M10d15h means a period of five years, two months, 10 days and 15 hours
     * Prefix the period with "-" to substract
     * @return {Date} a date object corresponding to the durationString parameter
     */
    var getTimeDuration = function(durationString) {
      var duration = moment(new Date());
      var substract = false;

      if (durationString) {
        // Check if there is a + or - before 'P' character, indicating if we have to add the duration
        // to now date, or remove it. No sign will indicate a +
        if (durationString.charAt(0) === '-') {
          substract = true;
          durationString = durationString.substring(1,durationString.length);
        } else if (durationString.charAt(0) === '+') {
          durationString = durationString.substring(1,durationString.length);
        }

        // Now parse, starting with the 'P' character indicating we parse a period
        if (durationString.charAt(0) === 'P') {
          durationString = durationString.substring(1,durationString.length);

          while (durationString.length > 1) {
            // Get first character which is not a number in the durationString
            var cpt = 0;
            while (!isNaN(parseInt(durationString[cpt]))) {cpt++;}
            var value = parseInt(durationString.substring(0, cpt));

            // Add or substract from now date
            duration = substract ?
              duration.subtract(value, durationString.charAt(cpt)) :
              duration.add(value, durationString.charAt(cpt));

            // Switch to next item to parse
            durationString = durationString.substring(cpt + 1,durationString.lenth);
          }
        }
      }

      return duration.toDate();
    };

    return {
      setMinRestriction: function(restrictions, dateField, unit) {
        setBorderRestriction('min', restrictions, dateField, unit);
      },
      setMaxRestriction: function(restrictions, dateField, unit) {
        setBorderRestriction('max', restrictions, dateField, unit);
      },
      setDurationRestriction: function(restrictions, dateField) {
        var max = -1;
        if (_.get(restrictions, 'maxDurationInclusive')) {
          max = getTimeDuration(_.get(restrictions, 'maxDurationInclusive'));
        }
        if (_.get(restrictions, 'maxDurationExclusive')) {
          max = getTimeDuration(_.get(restrictions, 'maxDurationExclusive'));
        }
        if (max !== -1) {
          dateField.datePickerConfig.max = max;
        }

        var min = -1;
        if (_.get(restrictions, 'minDurationInclusive')) {
          min = getTimeDuration(_.get(restrictions, 'minDurationInclusive'));
        }
        if (_.get(restrictions, 'minDurationExclusive')) {
          min = getTimeDuration(_.get(restrictions, 'minDurationExclusive'));
        }
        if (min !== -1) {
          dateField.datePickerConfig.min = min;
        }
      }
    };
  }
]);
angular.module('form.services').factory('formFormat', [

  function() {
    'use strict';

    // Function who format a phone number
    var formatPhone = function (phone) {

      var phoneFormated = phone;
      if (phone) {
        phone = phone.replace(/ |\.|\-/g, '');
        if (phone.indexOf('+33') === 0 && phone.length === 12) {
          phoneFormated = phone.substring(0, 3) + ' ' + phone.substring(3, 4) + ' ' + phone.substring(4, 6) + ' ' + phone.substring(6, 8) + ' ' + phone.substring(8, 10) + ' ' + phone.substring(10, 12);
        }
        if (phone.length === 10) {
          phoneFormated = phone.substring(0, 2) + ' ' + phone.substring(2, 4) + ' ' + phone.substring(4, 6) + ' ' + phone.substring(6, 8) + ' ' + phone.substring(8, 10);
        }
      }

      return phoneFormated;
    };

    /**
     * Capitalizes the first letter(s) of the given string
     * If the parameter contains spaces or '-', the word just after is also capitalized
     */
    var capitalizeFirst = function(word) {
      var capitalizedWord = null;
      if (word) {
        var pieces = word.split(' ');
        var i, j;
        for (i = 0; i < pieces.length; i++) {
          pieces[i] = pieces[i].toLowerCase();
          j = pieces[i].charAt(0).toUpperCase();
          pieces[i] = j + pieces[i].substr(1);
        }
        capitalizedWord = pieces.join(' ');

        pieces = capitalizedWord.split('-');
        for (i = 0; i < pieces.length; i++) {
          j = pieces[i].charAt(0).toUpperCase();
          pieces[i] = j + pieces[i].substr(1);
        }
        capitalizedWord = pieces.join('-');
      }
      return capitalizedWord;
    };

    return {
      capitalizeFirst: capitalizeFirst,
      formatPhone: formatPhone
    };
  }
]);

// This service can be used to configure some global behavior for the form components
// for a whole application

angular.module('form.services').provider('formPreferences', [function() {
  'use strict';
  var _this = this;
  var _config = {};

  this.config = function(config) {
    if (config === undefined) {
      return _config;
    } else {
      _config = config;
    }
  };

  this.$get = [function() {
    return {
      config: _this.config
    };
  }];

}]);

angular.module('form.services').factory('formUtils', [

  function() {
    'use strict';

    /**
		 * // a function to set a primitive in an object in order for it to be compatible
		 * with double data binding when passed to directives with isolated scopes
		 * Hackish but necessary when working on dynamic-field and siblings that bind primitive vars
		 * @param  {object} scope            Angular scope
		 * @param  {[type]} primitiveKey     [description]
		 * @param  {[type]} wrapperObjectKey [description]
		 * @return {[type]}                  [description]
		 */
    var wrapPrimitiveInObject = function(scope, primitiveKey, wrapperObjectKey) {
      scope[wrapperObjectKey] = scope[wrapperObjectKey] || {};
      scope[wrapperObjectKey][primitiveKey] = scope[primitiveKey];
      scope.$watch(primitiveKey, function() {
        scope[wrapperObjectKey][primitiveKey] = scope[primitiveKey];
      });
      scope.$watch(wrapperObjectKey + '.' + primitiveKey, function() {
        scope[primitiveKey] = scope[wrapperObjectKey][primitiveKey];
      });
    };

    /**
		 * Function who check is a value is an angular expression.
		 * Use for configuration properties who have expression angular
		 * @return {boolean} This string is a angular expression or not
		 */
    var checkIsAngularExpression = function(value) {
      var expression = false;
      if (typeof value === 'string' && (
        value.indexOf('==') !== -1 ||
					value.indexOf('!=') !== -1 ||
					value.indexOf('===') !== -1 ||
					value.indexOf('!==') !== -1 ||
					value.indexOf('>') !== -1 ||
					value.indexOf('<') !== -1 ||
					value.indexOf('>=') !== -1 ||
					value.indexOf('<=') !== -1)) {
        expression = true;
      }
      return expression;
    };

    return {
      wrapPrimitiveInObject: wrapPrimitiveInObject,
      checkIsAngularExpression: checkIsAngularExpression
    };
  }
]);
angular.module('form.services').factory('gridUtils', ['$compile', '$templateCache', '$log',

  function($compile, $templateCache, $log) {
    'use strict';

    function fieldEditor(options) {
      var tplSrc = options.template || $templateCache.get(options.templateUrl);

      if(!tplSrc) {
        $log.error('No template defined for editable grid field editor. Options: ', options);
        return;
      }

      var tpl = $compile(tplSrc, options.transclude);

      return function(container, kendoOptions) {
        var editorScope = options.scope.$new();
        editorScope.kendoOptions = kendoOptions;
        tpl(editorScope, null, null, options.transclude).appendTo(container);
        editorScope.$digest();
      };
    }

    return {
      fieldEditor: fieldEditor
    };
  }
]);

angular.module('form.services').factory('mapService', ['$q',

  function($q) {

    'use strict';

    var geoCoder = new google.maps.Geocoder();

    function _getAddress(coordonnees, simple) {

      // Construct search address

      var elements = [
        coordonnees.geographique.PointRemise,
        coordonnees.geographique.CodePostal,
        coordonnees.geographique.Localite.value,
        _.get(coordonnees, 'regionInsee.value', '')
      ];

      // Workaround for DOM-TOM
      // Google maps geocoder don't retreive any result if country 'FRANCE' is set for DOM-TOM
      if (coordonnees.geographique.CodePostal &&
        !_.startsWith(coordonnees.geographique.CodePostal, '97') &&
        !_.startsWith(coordonnees.geographique.CodePostal, '98')) {
        elements.push(coordonnees.geographique.Pays.value);
      }

      // If simple, we don't search the street
      if (simple) {
        elements.splice(0, 1);
      }

      return elements.join(', ');
    }

    function _getLocation(coordonnees, simple) {

      var defer = $q.defer();

      var address = _getAddress(coordonnees, simple);

      geoCoder.geocode({
        'address': address
      }, function(results, status) {

        switch (status) {
          case google.maps.GeocoderStatus.OK:
            var localisation = {
              latitude: results[0].geometry.location.lat(),
              longitude: results[0].geometry.location.lng()
            };
            defer.resolve(localisation);
            break;
          case google.maps.GeocoderStatus.ZERO_RESULTS:
            //If no results, we try a most general address (without street)
            defer.resolve(_getLocation(coordonnees, true));
            break;
          default:
            defer.reject(status);
        }
      });

      return defer.promise;
    }

    return {
      getLocation: _getLocation
    };
  }
]);

/**
 * @ngdoc service
 * @area api
 * @module form.services
 * @name mdmUtils
 * @description
 *
 *   Help dealing with mdm entries in entities and persistence objects
 */

angular.module('form.services').factory('mdmUtils', [

  function() {
    'use strict';

    /**
		 * Recursively clean mdm entries from an entity. They will be replaced by their references.
		 * If separataData is true the mdm entry will be kept but renamed with a _data suffix
		 *
		 * Beware that the object is modified and not cloned. If you wish to preserve the original object you should copy it before.
		 *
		 * @param  {Object} entity - The entity to clean
		 * @param  {Boolean} [separateData] - Set to true in order to keep the mdm entries withg _data suffixes
		 * @param {Object} [parent] - Used for recursivity, not needed by external users of the method
		 * @param {String} [key] - Used for recursivity, not needed by external users of the method
		 * @return {Object} the entity (not cloned)
		 */
    var cleanEntity = function(entity, separateData, parent, key) {
      for (var i in entity) {
        if (entity[i] !== null && typeof(entity[i]) === 'object') {
          if (entity[i]._mdmEntry) {
            // The current object is an mdm entry, let's process it
            if (separateData) {
              if (_.isArray(entity) && parent && key) {
                if (key.indexOf('_data') === -1) {
                  // If current iterated object is an array store its elements in a sibling array suffixed by _data
                  parent[key + '_data'] = parent[key + '_data'] || [];
                  parent[key + '_data'].push(angular.copy(entity[i]));
                }

              } else {
                if (i.indexOf('_data') === -1) {
                  // If current iterated object is not an array store its mdm entries with names suffixed by _data
                  entity[i + '_data'] = angular.copy(entity[i]);
                }
              }
            }
            entity[i] = entity[i].reference;
          } else {
            // recursivity
            cleanEntity(entity[i], separateData, entity, i);
          }
        }
      }
      return entity;
    };

    /**
		 * Return a mdm entry from either an object already matching an mdm entry or from a reference.
		 * Useful when initializing a model without being sure that the given data is raw or already populated with mdm entries.
		 *
		 * @param  {Object} source - A source object as filled by the mdm service
		 * @param  {String|Object} refOrEntry - Either the reference of a entry in the source or the complete entry
		 * @return {Object}
		 */
    var refOrEntry = function(source, refOrEntry) {
      if (refOrEntry) {
        if(typeof refOrEntry === 'object') {
          var reference; 
          var object; 
          if(_.get(refOrEntry,'expand.reference') !== undefined) { 
            reference = _.get(refOrEntry,'expand.reference'); 
            object = _.get(source, 'object.' + reference, refOrEntry); 
            return object; 
          } else { 
            reference = _.get(refOrEntry,'href'); 
            object = _.find(source.array, function(item) { 
              return _.get(item, 'href') === reference; 
            }); 
            return object; 
          } 
        }
        else if(typeof refOrEntry === 'string') {
          var array = _.get(source, 'array', []);
          var found = _.find(array, function(mdmObj) {
            return _.get(mdmObj, 'expand.reference') === refOrEntry;
          });
          return found || refOrEntry || null;
        }
      } else {
        return (source && source.object && source.object[refOrEntry]) || refOrEntry || null;
      }
    };

    return {
      cleanEntity: cleanEntity,
      refOrEntry: refOrEntry
    };
  }
]);
angular.module('form.services').factory('mdmService', ['configuration', '$q', '$http',
  function(configuration, $q, $http) {
    'use strict';

    var _tenantId;

    // Map to choose the mdm to call for a specific source name
    // If a source is not defined here it will be fetched from configuration.mdm.tenant
    function makeSources(config) {
      return {
        inseenaf2008: config.mdm.mgdis,
        mdmpays: config.mdm.mgdis
      };
    }
    var _sourceMdm = makeSources(configuration);

    // aliases for convenience.
    var aliases = {
      civilites: 'titrescivilites'
    };

    // basic memory cache, for MDM data it should be ok to use one
    var cache = {};

    // the promises builder functions that can be called to fill the MDM cache
    var promiseBuilders = {};

    /**
     * An utility function to quickly build promises on MDM sources
     *
     * @param {string} sourceName - a source name
     * @param {Object} [query] - optional query
     * @param {Function} [transform] - optional transform function that will be applied to each item of the list
     * @param {string} [mdmUrl] - optional data server management url
     * @param {boolean} [refresh] - option to avoid using cache
     * @return {Object} promise
     */
    var buildOdataPromise = function(sourceName, query, transform, mdmUrl, refresh) {
      var deferred = $q.defer();
      if (cache[sourceName] && !refresh) {
        deferred.resolve(cache[sourceName]);
      } else {
        // fetch a hundred documents max by default
        query = query || {};
        query.$top = query.$top || 500;

        // Do not return the non-active data
        // some collections don't have this property !
        if(['mdmpays', 'inseenaf2008', 'guichetsBancaires', 'etablissementsBancaires'].indexOf(sourceName) === -1) {
          query.$filter = query.$filter || '';
          if (query.$filter.length > 0) {
            query.$filter += ' and ';
          }
          query.$filter += 'actif ne false';
        }

        var url = (mdmUrl || _sourceMdm[sourceName] || configuration.mdm.tenant);
        if (_tenantId) {
          url = url.replace('{{tenantId}}', _tenantId);
        }

        // On n'accole pas la source name si ce dernier name est une url
        if (sourceName !== mdmUrl) {
          url += sourceName;
        }

        $http.get(url, {
          params: query
        }).then(
          // success callback
          function(data) {
            var documents = data.data.d.results;

            // Prepare 2 views of the source. One is an array
            //the second is an object with the 'reference' column as a key.

            var array = _.map(documents, function(doc) {

              var uri = doc.__metadata.uri;
              var href = uri.substr(0, uri.lastIndexOf('/') + 1) + encodeURIComponent(uri.substr(uri.lastIndexOf('/') + 1));

              var link = {
                href: href,
                expand: _.cloneDeep(doc)
              };

              link.expand.libelle = {
                value: link.expand.libelle
              };

              delete link.expand.__metadata;

              if (transform) {
                link = transform(link);
              }

              link.title = link.expand.libelle.value;

              return link;
            });

            var object = _.keyBy(array, 'expand.reference');

            cache[sourceName] = {
              array: array,
              object: object
            };
            deferred.resolve(cache[sourceName]);
          },
          // error callback
          function(error) {
            deferred.reject(error);
          });
      }
      return deferred.promise;
    };

    /**
     * Use to extract the type of promise we need to build from
     * a simple kay or a dataUrl
     */
    var _getPromiseByKey = function(key) {
      // Key exists (for compatibility)
      if(promiseBuilders[key]) {
        return promiseBuilders[key];
      }
      // If the dataUrl aim to a naf2008 source
      else if(key.indexOf('naf2008') > -1) {
        return promiseBuilders.naf;
      }
      // If the dataUrl aim to a mdmpays source
      else if(key.indexOf('mdmpays') > -1) {
        return promiseBuilders.pays;
      }
    };

    /**
     * Return a promise for a mdm object that will contain the requested lists
     *
     * @param {string[]|string} keys - keys of sources names
     * @param {string} [mdmUrl] - optional data server management url
     * @param {string} [query] - optional query
     * @param {boolean} [refresh] - option to avoid using cache
     * @return {Object} promise
     */
    var fill = function(keys, mdmUrl, query, refresh) {
      var promises = {};
      _.each(keys, function(key) {
        if (_getPromiseByKey(key)) {
          // either there is a dedicated builder
          promises[key] = _getPromiseByKey(key)(refresh);
        } else {
          // or we just assume the requested key is a source in the dataserver
          promises[key] = buildOdataPromise(aliases[key] || key, query || {
            $orderby: 'libelle asc'
          }, null, mdmUrl, refresh);
        }
      });
      // build a global promise based on all requested sources
      return $q.all(promises);
    };

    promiseBuilders.pays = function(refresh) {
      return buildOdataPromise('mdmpays',
        // the query to fetch countries
        {
          $filter: 'langue eq \'fr\'',
          $orderby: 'libpayscourtmaj asc',
          $top: 400
        },
        // the function to transform countries returned by the MDM into simpler objects
        function(pays) {
          pays.expand.reference = pays.expand.codpays2car;
          pays.expand.libelle = {
            value: pays.expand.libpayscourtmaj
          };
          return pays;
        }, null, refresh);
    };

    promiseBuilders.naf = function(refresh) {
      return buildOdataPromise('inseenaf2008', {
        $orderby: 'sousclasse asc',
        $top: 1000,
        $select: 'sousclasse, codesousclasse'
      },
      // the function to transform naf items returned by the MDM into simpler objects
      function(nafItem) {
        nafItem.expand.reference = nafItem.expand.codesousclasse;
        nafItem.expand.libelle = {
          value: nafItem.expand.codesousclasse + ' - ' + nafItem.expand.sousclasse
        };
        return nafItem;
      }, null, refresh);
    };

    promiseBuilders.localisantscog = function(refresh) {
      return buildOdataPromise('localisantscog', {
        $orderby: 'localisantmaj asc',
        $top: 100
      },
      // the function to transform insee items returned by the MDM into simpler objects
      function(inseeItem) {
        return inseeItem;
      }, null, refresh);
    };

    var fillFromFields = function(fields) {
      return fill(_.filter(_.map(fields, 'dataURL')));
    };

    // only return the fill function ? Or the cache too ?
    return {
      fill: fill,
      fillFromFields: fillFromFields,
      tenantId: function(tenantId) {
        _tenantId = tenantId;
      },
      configuration: function(config) {
        _sourceMdm = makeSources(config);
      }
    };
  }
]);

angular.module('form.services').factory('noCacheInterceptor', [

  function() {

    'use strict';

    return {
      /**
			 * Add a parameter named 'timestamp' to each GET request for disable cache on ajax request on IE.
			 * We hade also specific headers for disable cache on IE too.
			 * http://stackoverflow.com/questions/16098430/angular-ie-caching-issue-for-http
			 * @param  {[type]} config Configuration of ajax request
			 */
      'request': function(config) {

        // Disable cache only for get requests
        if (config.method === 'GET' && config.url && config.url.indexOf('.html') === -1 && config.url.indexOf('.htm') === -1) {
          var separator = config.url.indexOf('?') === -1 ? '?' : '&';
          config.url = config.url + separator + 'timestamp=' + new Date().getTime();

          // Initialize if not here
          if (!config.headers) {
            config.headers = {};
          }

          // Disable IE ajax request caching
          config.headers['If-Modified-Since'] = '0';
          config.headers['Cache-Control'] = 'no-cache';
          config.headers.Pragma = 'no-cache';
        }

        return config;
      }
    };
  }
]);
angular.module('form.services').factory('validationService', [

  function() {

    'use strict';

    return {
      /**
       * Vérifie la bonne écriture d'une adresse mail (RFC #822 for autorized characters, RFC#3696 who correct RFC #2821
       * for the maximal autorize length)
       * Also check http://jira.mgdis.fr/browse/PADA-12
       *
       * @param  {string} email Email à valider
       * @return {boolean}      True si l'email est valide, false sinon
       */
      verifyEmail: function(email) {

        if (!email || email.length === 0) {
          return true;
        } else {
          return validator.isEmail(email);
        }
      },
      /**
       * Méthode qui vérifie la validité du siret
       * @param  {String} siren Siren
       * @param  {String} nic   Nic
       * @return {Boolean} Indique si le siret est valide
       */
      verifySiret: function(siren, nic) {

        var validity = false;

        if (siren && nic && siren.length === 9 && nic.length === 5) {
          var siret = siren + nic;
          var somme = 0;
          var tmp;

          if (parseInt(siren) !== 356000000 || (parseInt(siren) === 356000000 && parseInt(nic, 10) === parseInt('00048', 10))) {
            for (var cpt = 0; cpt < siret.length; cpt++) {
              if ((cpt % 2) === 0) {
                tmp = parseInt(siret.charAt(cpt)) * 2;
                if (tmp > 9) {
                  tmp -= 9;
                }
              } else {
                tmp = parseInt(siret.charAt(cpt));
              }
              somme += parseInt(tmp);
            }
          } else {
            for (var i = 0; i < siret.length; i++) {
              somme += parseInt(siret.charAt(i));
            }
          }

          if ((somme % 10) === 0 || (parseInt(siren) === 356000000 && (somme % 5) === 0)) {
            validity = true;
          }
        }

        return validity;
      },
      verifyUrl: function(str) {

        if (str === undefined || str === null || str === '') {
          return true;
        } else {
          /* eslint camelcase: 0 */
          return validator.isURL(str, {require_protocol: true});
        }
      }
    };
  }
]);
// load configuration files from the resources of a portal to configure the views of our modules
// TODO: should not stay into the user module.

angular.module('form.services').factory('viewManagerService', ['$http', '$q', '$log', 'configuration',
  function($http, $q, $log, configuration) {
    'use strict';

    var basePath = configuration['resource-manager'].path;

    if (!basePath || !(angular.isString(basePath) || angular.isArray(basePath))) {
      $log.error('Missing key "resource-manager.path" in configuration, it should contain a string or an array of strings');
    }

    /**
     * Get a list of promises for download configurations on a module. We can hava multiple paths
     * @param  {[type]} moduleName Name of the module
     * @return {[type]}            List of promises
     */
    var getPromisesFilesConfiguration = function(moduleName) {
      var paths = angular.isArray(basePath) ? basePath : [basePath];

      return _.map(paths, function(path) {
        return $http({
          method: 'GET',
          url: path + '/' + moduleName + '.json',
          cache: true
        });
      });
    };

    // fetch a configuration for a view inside a complete configuration object
    var extractViewConfiguration = function(configuration, moduleName, viewId, namespace) {
      if (!configuration[moduleName] || !configuration[moduleName][viewId]) {
        $log.error('Missing configuration ' + moduleName + '.' + viewId);
        return null;
      }
      var viewConfiguration = configuration[moduleName][viewId];

      // namespaces will be used to fetch labels in i18n files for each fields
      viewConfiguration.ns = namespace || viewConfiguration.ns || moduleName + '.' + viewId;
      viewConfiguration.viewId = viewConfiguration.viewId || viewId;
      viewConfiguration.moduleName = viewConfiguration.moduleName || moduleName;

      if (viewConfiguration.fields) {
        _.each(viewConfiguration.fields, function(field) {
          field.ns = field.ns || viewConfiguration.ns;
        });
      }

      return viewConfiguration;
    };

    return {

      // Used for testing purpose otherwise see getViewConfiguration
      extractViewConfiguration: extractViewConfiguration,
      /**
       * Return a view configuration that can be the merge result of multiple subviews configuration
       * @param  {[type]} moduleName Name of the module
       * @param  {[type]} viewsId     Ids of the views configuration (a string or an array)
       * @return {[type]}            Configuration of the views
       */
      getViewConfiguration: function(moduleName, viewsId, namespace) {

        var deferred = $q.defer();

        $q.all(getPromisesFilesConfiguration(moduleName)).then(function(results) {

          var configuration = {};
          results.forEach(function(result) {
            $.extend(true, configuration, result.data);
          });

          var result = {};
          if (viewsId && angular.isArray(viewsId)) {
            // Extract multiple view configurations from a module
            for (var i in viewsId) {
              var viewId = viewsId[i];
              _.merge(result, extractViewConfiguration(configuration, moduleName, viewId, namespace));
            }
          } else if (viewsId) {
            // extract a single view configuration from a module
            result = extractViewConfiguration(configuration, moduleName, viewsId, namespace);
          } else {
            // get raw configuration object of a module
            result = configuration[moduleName];
          }

          deferred.resolve(result);
        },
        function(error) {
          deferred.reject(error);
        });

        return deferred.promise;
      }
    };
  }
]);
/**
 * @ngdoc module
 * @module form
 * @name form.filters
 * @description
 *
 *   Filters useful in forms.
 *
 *   This module is a part of {@link ../form/index.html form}.
 *
 */

angular.module('form.filters', []);
angular.module('form.filters').filter('replace', function() {
  'use strict';

  return function(string, params) {
    if (string && params && typeof(params) === 'object') {
      $.map(params, function(val, key) {
        string = string.replace(key, val);
      });
    }

    return string;
  };
});
angular.module('form.filters').filter('tryTranslate', ['$translate', function($translate) {
  'use strict';

  /**
  * Translate a label, and if no translation exists, return an empty string.
  */
  return function(lbKey) {
    return $translate.instant(lbKey) !== lbKey ? $translate.instant(lbKey) : '';
  };
}]);
